import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  Building, 
  RefreshCw, 
  Save,
  Shield,
  CreditCard,
  DollarSign,
  Clock,
  PlusCircle,
  Users,
  BookOpen,
  Briefcase,
  Upload,
  Camera,
  X,
  Check,
  Eye,
  AlertCircle,
  FileText,
  File, 
  Plus,
  Minus,
  Info, 
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Baby,
  School,
  GraduationCap,
  Calculator,
  Image as ImageIcon
} from 'lucide-react';

import { Toast } from "../components/ui/Toast";
import { LongToast, shouldUseLongToast } from "../components/ui/LongToast";
import { api } from '../services/api';
import { useFeatures } from '../contexts/FeaturesContext';
import CredentialsModal from './CredentialsModal';

import useHorairesConfig from '../hooks/useHorairesConfig';
import { useParametres } from '../contexts/ParametresContext';
import { usePermissionsFicheEmploye } from '../hooks/usePermissionsFicheEmploye';






const statutLegalOptions = [
  { value: 'CDI', label: 'CDI' },
  { value: 'CDD', label: 'CDD' },
  { value: 'STAGE', label: 'Stage' },
  { value: 'Autre', label: 'Autre' }
];

const contactRelationOptions = [
  { value: 'Conjoint(e)', label: 'Conjoint(e)' },
  { value: 'Parent', label: 'Parent' },
  { value: 'Enfant', label: 'Enfant' },
  { value: 'Frère/Sœur', label: 'Frère/Sœur' },
  { value: 'Ami(e)', label: 'Ami(e)' },
  { value: 'Autre', label: 'Autre' }
];

const employeeDocumentSections = [
  {
    type: 'piece_identite',
    title: "Pièce d'identité",
    hint: "PDF, JPG ou PNG jusqu'à 5MB",
    accept: '.pdf,.jpg,.jpeg,.png',
    multiple: false,
    required: true
  },
  {
    type: 'cv',
    title: 'CV',
    hint: "PDF, DOC ou DOCX jusqu'à 10MB",
    accept: '.pdf,.doc,.docx',
    multiple: false
  },
  {
    type: 'diplome',
    title: 'Diplômes',
    hint: "PDF, DOC, DOCX, JPG ou PNG jusqu'à 10MB, plusieurs fichiers possibles",
    accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png',
    multiple: true
  },
  {
    type: 'contrat',
    title: 'Contrat',
    hint: "PDF, DOC ou DOCX jusqu'à 10MB",
    accept: '.pdf,.doc,.docx',
    multiple: false
  },
  {
    type: 'other',
    title: 'Autres documents',
    hint: "PDF, DOC, DOCX, JPG ou PNG jusqu'à 10MB, plusieurs fichiers possibles",
    accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png',
    multiple: true
  }
];

const formatFileSize = (size = 0) => {
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
};

const normalizeEmployeePhones = (primaryPhone = '', phoneList = []) => {
  const values = Array.isArray(phoneList) ? phoneList : [];
  const normalized = [primaryPhone, ...values]
    .map(phone => String(phone || '').trim())
    .filter(Boolean);

  return [...new Set(normalized)];
};

const FormDropdown = ({ value, options, placeholder, onChange, icon: Icon }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedOption = options.find(option => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-left text-sm text-gray-700 shadow-sm transition-all hover:border-[#246990]/60 focus:outline-none focus:ring-2 focus:ring-[#246990]/20"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2">
          {Icon && <Icon size={16} className="shrink-0 text-[#246990]" />}
          <span className={selectedOption ? 'truncate text-gray-800' : 'truncate text-gray-400'}>
            {selectedOption?.label || placeholder}
          </span>
        </span>
        <ChevronDown size={16} className={`ml-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-gray-100 bg-white py-1 shadow-xl" role="listbox">
          {options.map(option => {
            const selected = option.value === value;

            return (
              <button
                type="button"
                key={option.value}
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${
                  selected
                    ? 'bg-[#eef7fb] font-medium text-[#246990]'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{option.label}</span>
                {selected && <Check size={16} className="text-[#246990]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Le nom des modules tel qu'un non-technicien le lit dans l'application.
const MODULES_EN_CLAIR = {
  ADMINISTRATION: 'Administration',
  RESSOURCE_HUMAINES: 'Ressources humaines',
  EMPLOYES: 'Employés',
  ELEVES: 'Élèves'
};

/**
 * ⚠️ LIRE UN RÉSULTAT DE LISTE SANS SE FAIRE PIÉGER PAR UN REFUS.
 *
 * `handleResponse` (src/services/api.js) NE LÈVE PAS sur un 400/403/409 :
 * elle REND `{ success: false, message, code, data: <corps du refus> }`. La
 * clé `data` porte donc un OBJET non vide — et c'est tout le piège : le
 * garde-fou historique `reponse.data || []` ne se déclenchait JAMAIS, parce
 * qu'un objet est truthy. L'état recevait un objet là où il attendait un
 * tableau, le `for...of` qui suivait levait « is not iterable », et le rendu
 * cassait ensuite sur `.map`.
 *
 * Relevé en production le 05/10/2026 sur l'écran « Nouvel employé » : les
 * employés du groupe « Administration Générale » recevaient un 403 sur
 * /api/groupes-securite et voyaient « Erreur lors du chargement des données »
 * sans aucune indication de la cause réelle.
 *
 * ⚠️ On exige donc une VRAIE liste, et on remonte le motif du refus en clair
 * pour pouvoir l'afficher. NE PAS revenir à `|| []` : il ne protège rien.
 */
const lireListe = (reponse) => {
  if (Array.isArray(reponse)) return { liste: reponse, refus: null };
  if (Array.isArray(reponse?.data)) return { liste: reponse.data, refus: null };
  if (!reponse || reponse.success !== false) return { liste: [], refus: null };

  const corps = reponse.data && typeof reponse.data === 'object' ? reponse.data : {};
  if ((reponse.code || corps.code) === 'MODULE_ACCESS_DENIED') {
    // ⚠️ `handleResponse` ne recopie PAS `requiredModule` au premier niveau :
    // seule la seconde moitié de cette ligne travaille vraiment. Ne pas la
    // « simplifier », le message perdrait le nom du module.
    const moduleExige = reponse.requiredModule || corps.requiredModule;
    const nom = MODULES_EN_CLAIR[moduleExige] || moduleExige || 'demandé';
    return { liste: [], refus: `votre compte n'a pas l'accès au module ${nom}` };
  }
  return {
    liste: [],
    refus: reponse.message || reponse.error || 'le serveur a refusé la lecture'
  };
};

const NewEmployeeForm = ({ onClose, annee_scolaire_id }) => {
  // Le numero de badge ne sert qu au pointage ; le scan RFID est une
  // extension vendue a part. C est le decoupage « pointage simple » de la
  // grille tarifaire : le badge manuel arrive avec le pointage, le lecteur
  // RFID reste au-dela.
  const { estActive } = useFeatures();
  const pointageOuvert = estActive('module.pointage');
  const rfidOuvert = estActive('cap.rfid');
  const droitsFiche = usePermissionsFicheEmploye();

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [rfidCapture, setRfidCapture] = useState('');
  const [rfidFieldActive, setRfidFieldActive] = useState(false);


  const [toastInfo, setToastInfo] = useState(null);

  useEffect(() => {
    if (!error) return;

    setToastInfo({
      message: error,
      type: 'error',
      long: String(error).length > 130
    });
    setError('');
  }, [error]);

  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [previewImage, setPreviewImage] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const countries = useMemo(() => countryList().getData(), []);
  const { horaireConfig, loading: horaireLoading } = useHorairesConfig();
const { parametres } = useParametres();

useEffect(() => {
  return () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };
}, []);

// ✅ FONCTION DE FORMATAGE MONNAIE DYNAMIQUE
const formatMontant = useCallback((montant) => {
  if (!montant || montant === 0) return `0 ${parametres.parametres_generaux?.monnaie_symbole || 'FCFA'}`;
  
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(montant) + ` ${parametres.parametres_generaux?.monnaie_symbole || 'FCFA'}`;
}, [parametres.parametres_generaux?.monnaie_symbole]);

  const [classesWithTaux, setClassesWithTaux] = useState([]);
  const [loadingClassesTaux, setLoadingClassesTaux] = useState(false);
  const [rolesByGroup, setRolesByGroup] = useState({});
  const [sections, setSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set());
  
  
  // États pour les listes de référence
  const [classes, setClasses] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [groupesSecurite, setGroupesSecurite] = useState([]);
  
  // État principal du formulaire avec les valeurs par défaut
  const [formData, setFormData] = useState({
  // Informations personnelles de base
  nom: '',
  prenom: '',
  sexe: '',
  sexe_autre: '',
  email: '',
  telephone: '',
  telephones: [''],
  date_naissance: '',
  lieu_naissance: '',
  nationalite: '',
  
  // Adresse
  adresse: '',
  ville: '',
  code_postal: '',
  pays: 'France',
  
  // Informations professionnelles
  numero_badge: '', 
  type_contrat: 'FIXE', // FIXE ou HORAIRE - pour badges et salaires
  horaire_standard_debut: '08:00',
  horaire_standard_fin: '17:00',
  departement_id: '',
  date_embauche: '', // Maintenu pour compatibilité
  
  // Nouvelles propriétés pour informations professionnelles étendues
  info_pro: {
    date_embauche: '',
    type_contrat: 'FIXE', // Synchronisé avec type_contrat principal
    statut_legal: '', // CDI, CDD, Stage, Autre - pour le légal
    type_contrat_autre: '', // Si "Autre" est sélectionné pour statut_legal
    date_fin_contrat: '',
    acces_application: false
  },
  
  // Contact d'urgence
  contact_urgence: {
    nom_prenom: '',
    relation: '',
    telephone: '',
    email: '',
    adresse: '',
    ville: '',
    code_postal: '',
    pays: ''
  },
  
  // Rôles et rémunération
  roles: [
    {
      id: Date.now(), // ID temporaire pour la gestion dans le formulaire
      groupe_securite_id: '',
      groupe_securite_nom: '',
      roles_selectionnes: [],
      mode_remuneration: 'MENSUEL', // Sera synchronisé automatiquement avec type_contrat
      salaire_base: '',
      a_primes: false,     // Nouvel attribut pour indiquer si l'employé aura des primes
      a_retenues: false,   // Nouvel attribut pour indiquer si l'employé aura des retenues
      classes_selectionnees: [],
      primes: {
        indemnite_responsabilite: '',
        prime_transport: '',
        prime_encouragement: '',
        prime_anciennete: '',
        indemnite_medicale: ''
      },
      retenues: {
        cnss: '',
        assurance: ''
      }
    }
  ],
  
  // Fichiers
  photo: null,
  documents: {
    cv: null,
    diplome: [],
    piece_identite: null,
    contrat: null,
    other: []
  }
});



  // Effets pour charger les données nécessaires
useEffect(() => {
  const fetchData = async () => {
    // ⚠️ AMENDEMENT : sans ce drapeau, le `catch` du bas écrase le motif réel.
    // Si le refus des groupes est annoncé puis que `checkEditMode()` lève pour
    // une autre raison, la phrase générique recouvrirait la seule explication
    // utile. Le catch ne parle donc que si personne n'a déjà parlé.
    let refusAnnonce = false;
    try {
      // Chargement des classes
      const classesResponse = await api.classes.getAll();
      setClasses(lireListe(classesResponse).liste);
      
      // Chargement des départements
      const departementsResponse = await api.departements.getAll();
      setDepartements(lireListe(departementsResponse).liste);


      // ✅ CHARGEMENT DES SECTIONS
      const sectionsResponse = await api.sections.getAll();
      // Ces trois lectures jettent volontairement le motif : aucune de leurs
      // routes n'est gardée par ADMINISTRATION, et trois messages concurrents
      // sur un même écran se chassent l'un l'autre. Ce qui compte ici, c'est
      // que l'état ne puisse plus recevoir qu'un tableau.
      const sectionsData = lireListe(sectionsResponse).liste;
      setSections(sectionsData);

      // ✅ SECTIONS FERMÉES PAR DÉFAUT
      setExpandedSections(new Set());
            
      
      // Chargement des groupes de sécurité
      // ⚠️ COTE EMPLOYES, et non `api.groupesSecurite.getAll()` : cette
      // derniere passe par une route gardee par la porte du module de
      // CONFIGURATION, que le groupe « Administration Generale » n'a aucune
      // raison de detenir. Voir le commentaire de `getGroupesSecurite`.
      const groupesResponse = await api.employes.getGroupesSecurite();
      const groupesLus = lireListe(groupesResponse);
      const groupes = groupesLus.liste;
      setGroupesSecurite(groupes);

      // ⚠️ C'EST ICI QUE L'ÉCRAN PLANTAIT. La route est gardée par
      // requireModuleAccess('ADMINISTRATION') : sur un 403,
      // `groupesResponse.data` portait l'objet du refus, le `|| []` ne tirait
      // pas, et le `for...of` quelques lignes plus bas levait. On dit
      // maintenant la vraie raison, et le formulaire tient debout.
      if (groupesLus.refus) {
        setError(
          `Les groupes de sécurité n'ont pas pu être chargés : ${groupesLus.refus}. `
          + "Le choix du groupe de sécurité restera vide et l'employé ne pourra pas "
          + "être enregistré. Demandez à un administrateur de vous accorder cet accès, "
          + 'puis rouvrez cet écran.'
        );
        refusAnnonce = true;
      }
      
      // ✅ CHANGER CETTE LIGNE : utiliser api.roles au lieu de api.nouveauRoles
      const rolesMap = {};
      for (const groupe of groupes) {
        try {
          console.log(`🎭 Chargement des rôles pour le groupe ${groupe.id} via /api/roles/groupe/${groupe.id}`);
          
          // ✅ LIGNE MODIFIÉE :
          // Cote employes aussi : meme raison, meme porte evitee. La reponse a
          // la MEME forme, `sous_licence` compris — la route reutilise le meme
          // controleur.
          const rolesResponse = await api.employes.getRolesParGroupe(groupe.id);
          
          // Un rôle rattaché à un module non souscrit n'est pas proposé
          // (backend2/config/licenceRoles.js) ; le serveur refuserait.
          const rolesLus = lireListe(rolesResponse);
          if (rolesLus.refus) {
            // Trace, pas message : cette route est gardée par la MÊME clé
            // ADMINISTRATION, donc elle refuse en même temps que les groupes.
            // Un second message ne dirait rien de plus.
            console.warn(`Rôles du groupe ${groupe.id} indisponibles : ${rolesLus.refus}`);
          }
          rolesMap[groupe.id] = rolesLus.liste.filter((r) => r.sous_licence !== false);
        } catch (roleError) {
          console.warn(`Impossible de charger les rôles pour le groupe ${groupe.id}:`, roleError);
          rolesMap[groupe.id] = [];
        }
      }
      setRolesByGroup(rolesMap);
      
      // Vérifier si on est en mode édition
      await checkEditMode();
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      if (!refusAnnonce) {
        setError(
          `Le chargement de l'écran a échoué : ${err?.message || 'erreur inconnue'}. `
          + 'Veuillez réessayer, et prévenez un administrateur si cela se reproduit.'
        );
      }
    }
  };
  
  fetchData();
}, [annee_scolaire_id]);

// Synchroniser automatiquement le mode de rémunération du rôle principal
useEffect(() => {
  const typeContrat = formData.type_contrat || formData.info_pro?.type_contrat;
  
  if (typeContrat && formData.roles.length > 0) {
    const modeAutomatique = typeContrat === 'HORAIRE' ? 'HORAIRE' : 'MENSUEL';
    
    // Mettre à jour seulement le rôle principal (index 0)
    if (formData.roles[0].mode_remuneration !== modeAutomatique) {
      setFormData(prev => ({
        ...prev,
        roles: prev.roles.map((role, index) => 
          index === 0 
            ? { ...role, mode_remuneration: modeAutomatique }
            : role
        )
      }));
    }
  }
}, [formData.type_contrat, formData.info_pro?.type_contrat]);


useEffect(() => {
  if (horaireConfig && !formData.horaire_standard_debut && !formData.horaire_standard_fin) {
    setFormData(prev => ({
      ...prev,
      horaire_standard_debut: horaireConfig.horaires_ecole_debut || '08:00',
      horaire_standard_fin: horaireConfig.horaires_ecole_fin || '17:00'
    }));
  }
}, [horaireConfig, formData.horaire_standard_debut, formData.horaire_standard_fin]);


// ✅ CHARGER LES CLASSES AVEC LEURS TAUX HORAIRES
useEffect(() => {
  const loadClassesWithTaux = async () => {
    try {
      setLoadingClassesTaux(true);
      
      // Essayer d'abord la route spécialisée
      const response = await api.classes.getClassesWithTauxHoraires();
      if (response.success) {
        console.log('✅ Classes avec taux horaires récupérées:', response.data);
        setClassesWithTaux(response.data);
      } else {
        console.warn('⚠️ Route taux-horaires échouée, utilisation des classes normales');
        // Fallback : utiliser les classes normales déjà chargées
        setClassesWithTaux(classes);
      }
    } catch (error) {
      console.error('❌ Erreur chargement classes avec taux:', error);
      // Fallback : utiliser les classes normales
      console.log('🔄 Fallback: utilisation des classes normales');
      setClassesWithTaux(classes);
    } finally {
      setLoadingClassesTaux(false);
    }
  };

  // Attendre que les classes soient chargées avant de charger les taux
  if (classes.length > 0) {
    loadClassesWithTaux();
  }
}, [classes]);



const countryOptions = useMemo(() => {
    return countryList().getData().map(country => ({
      value: country.value,
      label: country.label
    }));
  }, []);





  // Ajouter cette fonction pour gérer les changements dans le contact d'urgence
const handleContactUrgenceChange = (e) => {
  const { name, value } = e.target;
  setFormData(prev => ({
    ...prev,
    contact_urgence: {
      ...prev.contact_urgence,
      [name]: value
    }
  }));
};

const handleInfoProChange = (e) => {
  const { name, value, type, checked } = e.target;
  setFormData(prev => ({
    ...prev,
    info_pro: {
      ...(prev.info_pro || {}), // Utilisez un objet vide si info_pro est undefined
      [name]: type === 'checkbox' ? checked : value
    }
  }));
};

// À ajouter dans la liste des fonctions de gestion d'états
const copyEmployeeAddress = () => {
  setFormData(prev => ({
    ...prev,
    contact_urgence: {
      ...prev.contact_urgence,
      adresse: prev.adresse,
      ville: prev.ville,
      code_postal: prev.code_postal,
      pays: prev.pays
    }
  }));
};

const clearContactAddress = () => {
  setFormData(prev => ({
    ...prev,
    contact_urgence: {
      ...prev.contact_urgence,
      adresse: '',
      ville: '',
      code_postal: '',
      pays: ''
    }
  }));
};



// sans generation de contrat
const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatMissingFieldsMessage = (fields) => {
  const uniqueFields = [...new Set(fields.filter(Boolean))];
  if (uniqueFields.length === 0) return '';
  if (uniqueFields.length === 1) {
    return 'Veuillez renseigner le champ suivant : ' + uniqueFields[0] + '.';
  }
  return 'Veuillez renseigner les champs suivants : ' + uniqueFields.join(', ') + '.';
};

const getRoleDisplayName = (index) => index === 0 ? 'r\u00f4le principal' : 'r\u00f4le additionnel ' + index;

const getStep1ValidationMessage = () => {
  const missingFields = [];
  const infoPro = formData.info_pro || {};
  const contactUrgence = formData.contact_urgence || {};
  const selectedSexe = formData.sexe;

  if (isBlank(formData.nom)) missingFields.push('Nom');
  if (isBlank(formData.prenom)) missingFields.push('Pr\u00e9nom');
  if (isBlank(selectedSexe)) missingFields.push('Sexe');
  if (!isBlank(selectedSexe) && !['M', 'F'].includes(selectedSexe) && isBlank(formData.sexe_autre)) {
    missingFields.push('Pr\u00e9cision du sexe');
  }
  if (isBlank(formData.date_naissance)) missingFields.push('Date de naissance');
  if (isBlank(formData.lieu_naissance)) missingFields.push('Lieu de naissance');
  if (isBlank(formData.nationalite)) missingFields.push('Nationalit\u00e9');
  if (normalizeEmployeePhones(formData.telephone, formData.telephones).length === 0) missingFields.push('T\u00e9l\u00e9phone');
  if (isBlank(formData.adresse)) missingFields.push('Adresse');
  if (isBlank(formData.ville)) missingFields.push('Ville');
  if (isBlank(formData.code_postal)) missingFields.push('Code postal');
  if (isBlank(formData.pays)) missingFields.push('Pays');
  if (isBlank(infoPro.date_embauche) && isBlank(formData.date_embauche)) missingFields.push("Date d'embauche");
  if (isBlank(formData.type_contrat || infoPro.type_contrat)) missingFields.push('Type de contrat');
  if (isBlank(infoPro.statut_legal)) missingFields.push('Statut l\u00e9gal');
  if (isBlank(contactUrgence.nom_prenom)) missingFields.push("Contact d'urgence - nom et pr\u00e9nom");
  if (isBlank(contactUrgence.relation)) missingFields.push("Contact d'urgence - relation");
  if (isBlank(contactUrgence.telephone)) missingFields.push("Contact d'urgence - t\u00e9l\u00e9phone");
  if (isBlank(contactUrgence.adresse)) missingFields.push("Contact d'urgence - adresse");
  if (isBlank(contactUrgence.ville)) missingFields.push("Contact d'urgence - ville");
  if (isBlank(contactUrgence.code_postal)) missingFields.push("Contact d'urgence - code postal");
  if (isBlank(contactUrgence.pays)) missingFields.push("Contact d'urgence - pays");

  const missingMessage = formatMissingFieldsMessage(missingFields);
  if (missingMessage) return missingMessage;

  if (!isBlank(formData.email) && !emailRegex.test(String(formData.email).trim())) {
    return "L'adresse email de l'employ\u00e9 n'est pas valide.";
  }
  if (!isBlank(contactUrgence.email) && !emailRegex.test(String(contactUrgence.email).trim())) {
    return "L'adresse email du contact d'urgence n'est pas valide.";
  }

  return '';
};

const getStep2ValidationMessage = () => {
  console.log('Validation Step 2 - Donnees formData:', formData);

  const { roles } = formData;
  if (!roles || roles.length === 0) {
    console.log('Validation echouee: Aucun role defini');
    return 'Veuillez ajouter au moins un r\u00f4le.';
  }

  for (const [index, role] of roles.entries()) {
    const roleLabel = getRoleDisplayName(index);
    console.log('Validation role ' + index + ':', role);

    if (!role.groupe_securite_id) {
      console.log('Role ' + index + ': Groupe de securite manquant');
      return 'Veuillez s\u00e9lectionner le groupe de s\u00e9curit\u00e9 du ' + roleLabel + '.';
    }

    if (!role.roles_selectionnes || role.roles_selectionnes.length === 0) {
      console.log('Role ' + index + ': Aucun role metier selectionne');
      return 'Veuillez s\u00e9lectionner au moins un r\u00f4le m\u00e9tier pour le ' + roleLabel + '.';
    }

    if (role.mode_remuneration === 'MENSUEL') {
      if (!role.salaire_base || parseFloat(role.salaire_base) <= 0) {
        console.log('Role ' + index + ': Salaire de base manquant ou invalide');
        return 'Veuillez renseigner un salaire mensuel de base valide pour le ' + roleLabel + '.';
      }
      console.log('Role ' + index + ': Mode MENSUEL valide');
      continue;
    }

    if (role.mode_remuneration === 'HORAIRE') {
      if (!role.classes_selectionnees || role.classes_selectionnees.length === 0) {
        console.log('Role ' + index + ': Aucune classe selectionnee pour le mode HORAIRE');
        return 'Veuillez s\u00e9lectionner au moins une classe enseign\u00e9e pour le ' + roleLabel + '.';
      }

      if (!role.taux_horaires_predefinis || role.taux_horaires_predefinis.length === 0) {
        console.log('Role ' + index + ': Aucun taux horaire pre-defini');
        return "Aucun taux horaire n'est configur\u00e9 pour les classes du " + roleLabel + '. V\u00e9rifiez les taux dans le module classes et mati\u00e8res.';
      }

      const invalidRate = role.taux_horaires_predefinis.find(taux => (parseFloat(taux.heures_semaine) || 0) <= 0);
      if (invalidRate) {
        const classLabel = invalidRate.classe_nom || invalidRate.nom || 'la classe s\u00e9lectionn\u00e9e';
        console.log('Role ' + index + ': Heures par semaine manquantes ou invalides');
        return 'Veuillez renseigner les heures par semaine pour ' + classLabel + '.';
      }

      console.log('Role ' + index + ': Mode HORAIRE valide');
      continue;
    }

    console.log('Role ' + index + ': Mode de remuneration invalide');
    return 'Veuillez choisir le mode de r\u00e9mun\u00e9ration du ' + roleLabel + '.';
  }

  console.log('Resultat validation globale: VALIDE');
  return '';
};

const validateStep2Data = () => !getStep2ValidationMessage();

const getStep3ValidationMessage = () => {
  const missingFields = [];

  if (!formData.photo && !previewImage) missingFields.push("Photo de l'employ\u00e9");
  if (!hasDocument('piece_identite')) missingFields.push("Pi\u00e8ce d'identit\u00e9");

  return formatMissingFieldsMessage(missingFields);
};

const getCurrentStepValidationMessage = () => {
  if (currentStep === 1) return getStep1ValidationMessage();
  if (currentStep === 2) return getStep2ValidationMessage();
  if (currentStep === 3) return getStep3ValidationMessage();
  return '';
};

const hasHourlyContractAssignment = () => {
  const contractType = formData.type_contrat || formData.info_pro?.type_contrat;
  return contractType === 'HORAIRE' || (formData.roles || []).some(role => role.mode_remuneration === 'HORAIRE');
};

const goToNextStep = () => {
  console.log('goToNextStep appelee, etape actuelle:', currentStep);

  if (currentStep < 4) {
    const validationMessage = getCurrentStepValidationMessage();
    if (validationMessage) {
      console.log("Validation de l'etape " + currentStep + ' echouee:', validationMessage);
      setError(validationMessage);
      return;
    }
  }

  if (currentStep === 3) {
    console.log("A l'etape 3, passage a l'etape 4");
    setCurrentStep(4);
    window.scrollTo(0, 0);
    return;
  }

  if (currentStep < 4) {
    console.log("Passage de l'etape " + currentStep + " a l'etape " + (currentStep + 1));
    setCurrentStep(prevStep => prevStep + 1);
    window.scrollTo(0, 0);
  }
};



const autoFillTauxHoraires = useCallback((selectedClassesIds, roleIndex) => {
  if (!selectedClassesIds || selectedClassesIds.length === 0) return;

  console.log('🔍 Auto-remplissage pour classes:', selectedClassesIds);
  console.log('🔍 Classes avec taux disponibles:', classesWithTaux);

  const newTauxHoraires = [];
  
  selectedClassesIds.forEach(classeId => {
    // Chercher dans classesWithTaux (données de l'API spécialisée)
    const classe = classesWithTaux.find(c => c.id === parseInt(classeId));
    
    if (classe) {
      const tauxHoraire = parseFloat(classe.taux_horaire) || 0;
      console.log(`💰 Classe ${classe.nom}: taux = ${tauxHoraire} FCFA`);
      
      newTauxHoraires.push({
        classe_id: classe.id,
        classe_nom: classe.nom,
        taux_horaire: tauxHoraire,
        heures_semaine: 0 // À définir par l'utilisateur
      });
    } else {
      console.error(`❌ Classe ${classeId} introuvable dans classesWithTaux`);
    }
  });

  console.log('📋 Taux horaires générés:', newTauxHoraires);

  // Mettre à jour le rôle avec les taux pré-remplis
  setFormData(prev => ({
    ...prev,
    roles: prev.roles.map((role, index) => 
      index === roleIndex 
        ? { 
            ...role, 
            taux_horaires_predefinis: newTauxHoraires,
            classes_selectionnees: selectedClassesIds
          }
        : role
    )
  }));
}, [classesWithTaux]);


const toggleExpansion = (sectionId) => {
  setExpandedSections(prev => {
    const newSet = new Set(prev);
    if (newSet.has(sectionId)) {
      newSet.delete(sectionId);
    } else {
      newSet.add(sectionId);
    }
    return newSet;
  });
};


  // Vérification du mode édition
  const checkEditMode = async () => {
    const editingId = localStorage.getItem('editingEmployeeId');
    if (editingId) {
      setLoading(true);
      try {
        const employeeData = await api.employes.getById(editingId);
        if (employeeData) {
          setIsEditing(true);
          // Remplir le formulaire avec les données existantes
          setFormData({
            nom: employeeData.nom,
            prenom: employeeData.prenom,
            email: employeeData.email,
            telephone: employeeData.telephone,
            telephones: Array.isArray(employeeData.telephones) && employeeData.telephones.length > 0
              ? employeeData.telephones
              : [employeeData.telephone || ''],
            date_naissance: employeeData.date_naissance,
            adresse: employeeData.adresse,
            ville: employeeData.ville,
            code_postal: employeeData.code_postal,
            pays: employeeData.pays,
            departement_id: employeeData.departement_id,
            date_embauche: employeeData.date_embauche,
            roles: employeeData.roles || [formData.roles[0]],
            photo: null,
            documents: {
              cv: null,
              diplome: null,
              piece_identite: null,
              contrat: null
            }
          });
          
          // Si une photo existe, prévisualiser
          if (employeeData.photo_url) {
            setPreviewImage(employeeData.photo_url);
          }
        }
      } catch (err) {
        console.error("Erreur lors du chargement des données de l'employé:", err);
        setError("Erreur lors du chargement des données de l'employé");
      } finally {
        setLoading(false);
      }
    }
  };


  const handleHasBonusChange = (index, field, value) => {
    const updatedRoles = [...formData.roles];
    updatedRoles[index][field] = value;
    
    // Si l'option "non" est sélectionnée, réinitialiser les valeurs correspondantes
    if (!value) {
      if (field === 'a_primes') {
        updatedRoles[index].primes = {
          indemnite_responsabilite: '',
          prime_transport: '',
          prime_encouragement: '',
          prime_anciennete: '',
          indemnite_medicale: ''
        };
      } else if (field === 'a_retenues') {
        updatedRoles[index].retenues = {
          cnss: '',
          assurance: ''
        };
      }
    }
    
    setFormData(prev => ({ ...prev, roles: updatedRoles }));
  };


  
  // Gestionnaires d'événements
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getPhoneFields = () => (
    Array.isArray(formData.telephones) && formData.telephones.length > 0
      ? formData.telephones
      : [formData.telephone || '']
  );

  const handleEmployeePhoneChange = (index, value) => {
    const phones = getPhoneFields();
    const updatedPhones = phones.map((phone, phoneIndex) => phoneIndex === index ? value : phone);

    setFormData(prev => ({
      ...prev,
      telephone: updatedPhones[0] || '',
      telephones: updatedPhones
    }));
  };

  const addEmployeePhone = () => {
    setFormData(prev => {
      const phones = Array.isArray(prev.telephones) && prev.telephones.length > 0
        ? prev.telephones
        : [prev.telephone || ''];

      return {
        ...prev,
        telephones: [...phones, '']
      };
    });
  };

  const removeEmployeePhone = (index) => {
    setFormData(prev => {
      const phones = Array.isArray(prev.telephones) && prev.telephones.length > 0
        ? prev.telephones
        : [prev.telephone || ''];
      const updatedPhones = phones.filter((_, phoneIndex) => phoneIndex !== index);
      const nextPhones = updatedPhones.length > 0 ? updatedPhones : [''];

      return {
        ...prev,
        telephone: nextPhones[0] || '',
        telephones: nextPhones
      };
    });
  };
  const handleRoleChange = (index, field, value) => {
  const updatedRoles = [...formData.roles];
  
  if (field === 'groupe_securite_id') {
    const groupe = groupesSecurite.find(g => g.id === value);
    updatedRoles[index] = {
      ...updatedRoles[index],
      [field]: value,
      groupe_securite_nom: groupe ? groupe.nom : ''
    };
  } else {
    updatedRoles[index] = {
      ...updatedRoles[index],
      [field]: value
    };
  }
  
  setFormData(prev => ({ ...prev, roles: updatedRoles }));

  // ✅ AUTO-REMPLISSAGE QUAND MODE HORAIRE + CLASSES SÉLECTIONNÉES
  if (field === 'classes_selectionnees' && 
      updatedRoles[index]?.mode_remuneration === 'HORAIRE') {
    setTimeout(() => autoFillTauxHoraires(value, index), 100);
  }
  
  // ✅ AUTO-REMPLISSAGE QUAND PASSAGE EN MODE HORAIRE
  if (field === 'mode_remuneration' && value === 'HORAIRE' && 
      updatedRoles[index]?.classes_selectionnees?.length > 0) {
    setTimeout(() => autoFillTauxHoraires(updatedRoles[index].classes_selectionnees, index), 100);
  }
};
 const handleAddRole = () => {
  // Déterminer le mode de rémunération par défaut selon le type de contrat
  const defaultMode = (formData.type_contrat === 'HORAIRE' || formData.info_pro?.type_contrat === 'HORAIRE') 
    ? 'HORAIRE' 
    : 'MENSUEL';
  
  const newRole = {
    id: Date.now(),
    groupe_securite_id: '',
    groupe_securite_nom: '',
    roles_selectionnes: [],
    mode_remuneration: defaultMode, // Utilise le mode automatique
    salaire_base: '',
    classes_selectionnees: [],
    primes: {
      indemnite_responsabilite: '',
      prime_transport: '',
      prime_encouragement: '',
      prime_anciennete: '',
      indemnite_medicale: ''
    },
    retenues: {
      cnss: '',
      assurance: ''
    }
  };
  
  setFormData(prev => ({
    ...prev,
    roles: [...prev.roles, newRole]
  }));
};
  
  const handleRemoveRole = (index) => {
    if (formData.roles.length > 1) {
      const updatedRoles = formData.roles.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, roles: updatedRoles }));
    } else {
      setError('Un employé doit avoir au moins un rôle');
    }
  };
  
 const handleClassToggle = (roleIndex, classeId) => {
  const updatedRoles = [...formData.roles];
  const role = updatedRoles[roleIndex];
  
  // Vérifier si la classe est déjà sélectionnée
  const isSelected = role.classes_selectionnees?.includes(classeId);
  
  let newSelectedIds = [];
  
  if (isSelected) {
    // Retire la classe
    newSelectedIds = role.classes_selectionnees.filter(id => id !== classeId);
  } else {
    // Ajoute la classe
    newSelectedIds = [...(role.classes_selectionnees || []), classeId];
  }
  
  // Mettre à jour les classes sélectionnées
  updatedRoles[roleIndex].classes_selectionnees = newSelectedIds;
  setFormData(prev => ({ ...prev, roles: updatedRoles }));

  // ✅ AUTO-REMPLISSAGE AUTOMATIQUE POUR MODE HORAIRE
  if (role.mode_remuneration === 'HORAIRE') {
    console.log('🔄 Mode HORAIRE - Auto-remplissage pour:', newSelectedIds);
    setTimeout(() => autoFillTauxHoraires(newSelectedIds, roleIndex), 200);
  }
};



  
  //const handleClassDetailChange = (roleIndex, classeIndex, field, value) => {
   // const updatedRoles = [...formData.roles];
   // updatedRoles[roleIndex].classes_selectionnees[classeIndex][field] = value;
   // setFormData(prev => ({ ...prev, roles: updatedRoles }));
 // };
  
  const handleRemunerationChange = (e, roleIndex, category, field) => {
    const { value } = e.target;
    const updatedRoles = [...formData.roles];
    
    updatedRoles[roleIndex][category][field] = value;
    setFormData(prev => ({ ...prev, roles: updatedRoles }));
  };
  
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError("La taille de l'image ne doit pas dépasser 5MB");
      e.target.value = '';
      return;
    }

    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      setError("Seuls les formats JPG et PNG sont acceptés");
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
      setFormData(prev => ({ ...prev, photo: file }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("La caméra n'est pas disponible dans ce navigateur.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false
      });

      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 0);
    } catch (error) {
      console.error('Erreur ouverture caméra:', error);
      setCameraError("Impossible d'ouvrir la caméra. Vérifiez les autorisations du navigateur.");
    }
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    const sourceWidth = video.videoWidth || 640;
    const sourceHeight = video.videoHeight || 480;
    const cropSize = Math.min(sourceWidth, sourceHeight);
    const sourceX = Math.max(0, (sourceWidth - cropSize) / 2);
    const sourceY = Math.max(0, (sourceHeight - cropSize) / 2);

    canvas.width = 720;
    canvas.height = 720;

    const context = canvas.getContext('2d');
    context.drawImage(video, sourceX, sourceY, cropSize, cropSize, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new window.File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFormData(prev => ({ ...prev, photo: file }));
      setPreviewImage(URL.createObjectURL(blob));
      stopCamera();
    }, 'image/jpeg', 0.9);
  };

  const clearPhoto = () => {
    setPreviewImage(null);
    setFormData(prev => ({ ...prev, photo: null }));
    stopCamera();
  };

  const getDocumentFiles = (type) => {
    const value = formData.documents?.[type];
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
  };

  const getDocumentConfig = (type) => (
    employeeDocumentSections.find(section => section.type === type) || {}
  );

  const validateDocumentFile = (file, type) => {
    const config = getDocumentConfig(type);
    const maxSize = type === 'piece_identite' ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`La taille du fichier ne doit pas dépasser ${maxSize === 10 * 1024 * 1024 ? '10MB' : '5MB'}`);
      return false;
    }

    const accept = String(config.accept || '').split(',').map(ext => ext.trim().toLowerCase()).filter(Boolean);
    const fileName = String(file.name || '').toLowerCase();
    if (accept.length > 0 && !accept.some(ext => fileName.endsWith(ext))) {
      setError('Format de fichier non pris en charge pour ce document.');
      return false;
    }

    return true;
  };

  const handleFileChange = (e, type, options = {}) => {
    if (type === 'photo') {
      handlePhotoChange(e);
      return;
    }

    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter(file => validateDocumentFile(file, type));
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    const multiple = options.multiple === true;
    setFormData(prev => {
      const current = prev.documents?.[type];
      const currentFiles = Array.isArray(current) ? current : (current ? [current] : []);

      return {
        ...prev,
        documents: {
          ...prev.documents,
          [type]: multiple ? [...currentFiles, ...validFiles] : validFiles[0]
        }
      };
    });
    e.target.value = '';
  };

  const removeDocumentFile = (type, index) => {
    const config = getDocumentConfig(type);
    setFormData(prev => {
      const current = prev.documents?.[type];
      if (Array.isArray(current) || config.multiple) {
        const nextFiles = (Array.isArray(current) ? current : (current ? [current] : []))
          .filter((_, fileIndex) => fileIndex !== index);

        return { ...prev, documents: { ...prev.documents, [type]: nextFiles } };
      }

      return { ...prev, documents: { ...prev.documents, [type]: null } };
    });
  };

  const renderDocumentList = (files, onRemove) => (
    <div className="mt-3 space-y-2">
      {files.map((file, index) => (
        <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-md bg-[#e7f3f8] text-[#246990] flex items-center justify-center flex-shrink-0">
              <FileText size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
            </div>
          </div>
          <button type="button" onClick={() => onRemove(index)} className="p-1.5 rounded-md text-red-500 hover:bg-red-50 flex-shrink-0" title="Retirer le document">
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );

  const hasDocument = (type) => getDocumentFiles(type).length > 0;

  const getPhoneSummary = () => {
    const phones = normalizeEmployeePhones(formData.telephone, formData.telephones);
    return phones.length > 0 ? phones.join(' / ') : '-';
  };

  const getDocumentSummaryLabel = (type) => {
    const files = getDocumentFiles(type);
    if (files.length === 0) return '';
    if (files.length > 1) return `${files.length} fichiers`;

    const name = files[0]?.name || '';
    return name.length > 18 ? `${name.substring(0, 15)}...` : name;
  };

  // Calculs pour l'estimation de la rémunération en mode horaire
  const estimations = useMemo(() => {
    return formData.roles.map(role => {
      if (role.mode_remuneration !== 'HORAIRE' || role.classes_selectionnees.length === 0) {
        return null;
      }
      
      // Calcul des détails par classe
      const classeDetails = role.classes_selectionnees.map(classe => {
        const tauxHoraire = parseFloat(classe.taux_horaire) || 0;
        const heuresSemaine = parseFloat(classe.heures_semaine) || 0;
        
        // Calculs selon les formules fournies
        const salairePourClasse = {
          journalier: (tauxHoraire * heuresSemaine) / 5, // basé sur 5 jours/semaine
          hebdomadaire: tauxHoraire * heuresSemaine,
          mensuel: tauxHoraire * heuresSemaine * 4.33 // 4.33 semaines/mois en moyenne
        };
        
        return {
          ...classe,
          tauxHoraire,
          heuresSemaine,
          salairePourClasse
        };
      });
      
      // Calcul des totaux
      const totalJournalier = classeDetails.reduce((sum, classe) => sum + classe.salairePourClasse.journalier, 0);
      const totalHebdomadaire = classeDetails.reduce((sum, classe) => sum + classe.salairePourClasse.hebdomadaire, 0);
      const totalMensuel = classeDetails.reduce((sum, classe) => sum + classe.salairePourClasse.mensuel, 0);
      
      // Moyenne d'heures par semaine (pour l'affichage de la note explicative)
      const heuresMoyennes = classeDetails.length > 0
        ? (classeDetails.reduce((sum, c) => sum + c.heuresSemaine, 0) / classeDetails.length).toFixed(1)
        : 0;
      
      return {
        classeDetails,
        totalJournalier,
        totalHebdomadaire,
        totalMensuel,
        heuresMoyennes
      };
    });
  }, [formData.roles]);


  
  // Fonction de soumission
  // Remplacez la méthode handleSubmit dans NewEmployeeForm.jsx
 const handleSubmit = async (e) => {
  console.log("handleSubmit appelée, étape actuelle:", currentStep);
  e.preventDefault();

  if (loading) return;

  try {
    setLoading(true);
    setError('');
    
    // Validation des champs obligatoires
    const step1ValidationMessage = getStep1ValidationMessage();
    if (step1ValidationMessage) {
      setError(step1ValidationMessage);
      setLoading(false);
      return;
    }

    const step2ValidationMessage = getStep2ValidationMessage();
    if (step2ValidationMessage) {
      setError(step2ValidationMessage);
      setLoading(false);
      return;
    }

    const step3ValidationMessage = getStep3ValidationMessage();
    if (step3ValidationMessage) {
      setError(step3ValidationMessage);
      setLoading(false);
      return;
    }

    const cleanTelephones = normalizeEmployeePhones(formData.telephone, formData.telephones);
    if (cleanTelephones.length === 0) {
      setError("Veuillez renseigner au moins un numéro de téléphone.");
      setLoading(false);
      return;
    }
    // Sanitisation des données
    const sanitizedData = sanitizeFormData(formData);
    
    // Log des données sanitisées
    console.log("Données sanitisées:", sanitizedData);
    
    // Construction du FormData pour l'envoi des fichiers
    const data = new FormData();
    
    // Ajout des données de base
    const { photo, documents, ...employeeData } = sanitizedData;
    
    // Vérification des données avant conversion en JSON
    console.log("Données employé avant JSON:", employeeData);
    
    // Conversion en JSON avec traitement spécial des undefined
    const jsonData = JSON.stringify(employeeData, (key, value) => 
      value === undefined ? null : value
    );
    
    // Vérification que le JSON est valide
    try {
      const parsedData = JSON.parse(jsonData);
      console.log("JSON est valide, champs clés:", {
        nom: parsedData.nom,
        prenom: parsedData.prenom,
        email: parsedData.email
      });
    } catch (jsonError) {
      console.error("JSON invalide:", jsonError);
      throw new Error("Erreur dans la conversion JSON des données");
    }
    
    // Ajout des données au FormData
    data.append('data', jsonData);
    
    // Maintenant on peut lire data.get('data')
    console.log("Données JSON qui seront envoyées:", data.get('data'));
    
    // Vérification du contenu du FormData
    console.log("FormData contient:", Array.from(data.entries())
      .map(e => e[0] === 'data' 
        ? [e[0], "Longueur: " + e[1].length] 
        : e
      )
    );
    
    // Ajout de la photo
    if (photo) {
      data.append('photo', photo);
      console.log("Photo ajoutée au FormData");
    }
    
    // Ajout des documents
    if (documents) {
      Object.entries(documents).forEach(([key, value]) => {
        const files = Array.isArray(value) ? value : (value ? [value] : []);
        files.forEach((file) => {
          if (file) {
            data.append(key, file);
            console.log(`Document ${key} ajouté au FormData`);
          }
        });
      });
    }
    
    // ✅ CORRECTION : URL RELATIVE au lieu de l'URL absolue
    // Utiliser la couche API pour transmettre tenant et annee scolaire
    console.log("Preparation envoi: /api/employes");

    const response = await api.employes.create(data);
    
    // Traitement de la réponse
    if (response.success) {
      if (sanitizedData.info_pro?.acces_application && 
        response.data && response.data.credentials) {
        setCredentials(response.data.credentials);
        setShowCredentials(true);
      } else {
        setToastInfo({
          message: `Employé ${isEditing ? 'modifié' : 'créé'} avec succès`,
          type: 'success'
        });
        
        if (isEditing) {
          localStorage.removeItem('editingEmployeeId');
          navigate('/liste-employes');
        } else {
          resetForm();
        }
      }
    } else {
      throw new Error(response.message || 'Échec de l\'opération');
    }
  } catch (err) {
    console.error('Erreur lors de la création de l\'employé:', err);
    setError(`Erreur lors de la création de l'employé: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

  const sanitizeFormData = (data) => {
  // Fonction récursive pour traiter les objets imbriqués
  const sanitizeObject = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    
    // Si c'est un tableau, sanitiser chaque élément
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeObject(item));
    }
    
    // Vérification sécurisée pour File
    if (obj && typeof obj === 'object' && obj.constructor && obj.constructor.name === 'File') {
      return obj;
    }
    
    // Pour les objets, sanitiser chaque propriété
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = sanitizeObject(value);
    }
    
    return result;
  };
  
  // Copie profonde et sanitisation
  const sanitized = JSON.parse(
    JSON.stringify(data, (key, value) => {
      // Ignorer les objets de type File lors de la sérialisation JSON
      if (value && typeof value === 'object' && value.constructor && 
          value.constructor.name === 'File') {
        return undefined; // Les objets File seront exclus du JSON
      }
      
      // Convertir undefined en null
      if (value === undefined) return null;
      
      // Sanitiser les chaînes vides
      if (value === '') return null;
      
      // Gérer spécifiquement les dates
      if (key.includes('date_') && value) {
        // Vérifier si c'est une date valide
        const date = new Date(value);
        if (isNaN(date.getTime())) return null;
      }
      
      return value;
    })
  );
  
  // Recréer l'objet original avec les objets File
  const result = { ...sanitized };

  const cleanTelephones = normalizeEmployeePhones(result.telephone, result.telephones);
  result.telephones = cleanTelephones;
  result.telephone = cleanTelephones[0] || '';
  // Restaurer la photo si elle existe
  if (data.photo && typeof data.photo === 'object' && 
      data.photo.constructor && data.photo.constructor.name === 'File') {
    result.photo = data.photo;
  }
  
  // Restaurer les documents
  if (data.documents) {
    result.documents = { ...sanitized.documents };
    for (const [key, value] of Object.entries(data.documents)) {
      if (Array.isArray(value)) {
        result.documents[key] = value.filter(file => file && typeof file === 'object' && file.constructor && file.constructor.name === 'File');
      } else if (value && typeof value === 'object' && 
          value.constructor && value.constructor.name === 'File') {
        result.documents[key] = value;
      }
    }
  }
  
  
  // ✅ MAPPER LES TAUX PRÉ-DÉFINIS VERS LE FORMAT ATTENDU
if (result.roles && Array.isArray(result.roles)) {
  result.roles = result.roles.map(role => ({
    ...role,
    // Mapper les taux pré-définis (lecture seule) vers le format API
    taux_horaires: role.taux_horaires_predefinis || [],
    // Supprimer les champs temporaires et les anciennes structures
    taux_horaires_predefinis: undefined,
    classes_selectionnees: undefined // Plus besoin de cette structure
  }));
}
  
  return result;
};



  // Fonction auxiliaire pour vérifier si un objet est vide
  const isEmptyObject = (obj) => {
    return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
  };
  
  // Fonction pour obtenir une valeur sécurisée
  const safeValue = (value, defaultValue = null) => {
    if (value === undefined || value === null || value === '') return defaultValue;
    return value;
  };
  

  // Réinitialisation du formulaire
  const resetForm = () => {
  if (isEditing) {
    localStorage.removeItem('editingEmployeeId');
    navigate('/liste-employes');
    return;
  }

  setFormData({
    // Informations personnelles de base
    nom: '',
    prenom: '',
    sexe: '',
    sexe_autre: '',
    email: '',
    telephone: '',
    telephones: [''],
    date_naissance: '',
    lieu_naissance: '',
    nationalite: '',
    
    // Adresse
    adresse: '',
    ville: '',
    code_postal: '',
    pays: 'France',
    
    // Informations professionnelles
    numero_badge: '', 
    rfid_uid: '',
    type_contrat: 'FIXE',
    horaire_standard_debut: '08:00',
    horaire_standard_fin: '17:00',
    departement_id: '',
    date_embauche: '',
    
    // Info pro étendues
    info_pro: {
      date_embauche: '',
     
      type_contrat: 'FIXE',
      statut_legal: '',
      type_contrat_autre: '',
      date_fin_contrat: '',
      acces_application: false
    },
    
    // Contact d'urgence
    contact_urgence: {
      nom_prenom: '',
      relation: '',
      telephone: '',
      email: '',
      adresse: '',
      ville: '',
      code_postal: '',
      pays: ''
    },
    
    // Rôles
    roles: [
      {
        id: Date.now(),
        groupe_securite_id: '',
        groupe_securite_nom: '',
        roles_selectionnes: [],
        mode_remuneration: 'MENSUEL',
        salaire_base: '',
        a_primes: false,
        a_retenues: false,
        classes_selectionnees: [],
        primes: {
          indemnite_responsabilite: '',
          prime_transport: '',
          prime_encouragement: '',
          prime_anciennete: '',
          indemnite_medicale: ''
        },
        retenues: {
          cnss: '',
          assurance: ''
        }
      }
    ],
    
    // Fichiers
    photo: null,
    documents: {
      cv: null,
      diplome: [],
      piece_identite: null,
      contrat: null,
      other: []
    }
  });
  
  setPreviewImage(null);
  setCameraError(null);
  if (streamRef.current) {
    streamRef.current.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }
  setCameraActive(false);
  setCurrentStep(1);
  setError('');
};
  // Les erreurs du formulaire sont maintenant affichees par Toast/LongToast.
  const renderError = () => null;

  const calculateContractDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Vérifier que les dates sont valides
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return "Dates invalides";
    }
    
    // Vérifier que la date de fin est après la date de début
    if (end <= start) {
      return "La date de fin doit être postérieure à la date de début";
    }
    
    // Calculer la différence en jours
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Calculer les mois (approximatif)
    const months = Math.floor(diffDays / 30);
    const remainingDays = diffDays % 30;
    
    // Formatage du résultat
    if (months > 0 && remainingDays > 0) {
      return `${months} mois et ${remainingDays} jour${remainingDays > 1 ? 's' : ''}`;
    } else if (months > 0) {
      return `${months} mois`;
    } else {
      return `${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    }
  };


  // ============================================================================
// GESTION CAPTURE RFID
// ============================================================================
const handleRfidKeyDown = (e) => {
  // Ignorer les touches de modification
  if (e.keyCode === 16 || e.keyCode === 17 || e.keyCode === 18) return;
  
  // Si c'est Enter avec un badge, enregistrer
  if (e.keyCode === 13 && rfidCapture.trim()) {
    e.preventDefault();
    setFormData(prev => ({ ...prev, rfid_uid: rfidCapture.trim() }));
    setRfidCapture('');
    
    // Toast de confirmation
    setToastInfo({
      message: `Badge RFID capturé : ${rfidCapture.trim()}`,
      type: 'success'
    });
    
    return;
  }
  
  // Ignorer tout ce qui n'est pas alphanumérique
  if (!((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 65 && e.keyCode <= 90))) {
    e.preventDefault();
    return;
  }
  
  // Ajouter le caractère
  const char = String.fromCharCode(e.keyCode);
  setRfidCapture(prev => prev + char);
  
  // ✅ Auto-reset après 500ms d'inactivité (pour ignorer saisie manuelle)
  clearTimeout(window.rfidFormTimeout);
  window.rfidFormTimeout = setTimeout(() => {
    setRfidCapture('');
  }, 500);
  
  e.preventDefault();
};



  // Rendu des étapes du formulaire
  const renderStep1 = () => {
    // Obtenir la liste des pays
  
    return (
      <div className="space-y-5">
        {renderError()}
        
        {/* Informations personnelles - Version améliorée */}
        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5">
            <div className="p-2 bg-[#246990] rounded-full mr-4">
              <User className="text-white" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-[#246990] text-lg">Informations personnelles</h3>
              <p className="text-sm text-gray-600">Informations de base concernant l'employé.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            {/* Nom */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Nom <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
            
            {/* Prénom */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Prénom <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
            
            {/* Sexe (Nouveau champ) */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Sexe <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-3">
                <label className={`flex items-center p-2.5 border rounded-lg cursor-pointer ${
                  formData.sexe === 'M' 
                    ? 'bg-[#eef7fb] border-[#246990] text-[#246990]' 
                    : 'bg-white border-gray-200 text-gray-700'
                }`}>
                  <input
                    type="radio"
                    name="sexe"
                    value="M"
                    checked={formData.sexe === 'M'}
                    onChange={handleInputChange}
                    className="hidden"
                    required
                  />
                  <Check className={`mr-2 ${formData.sexe === 'M' ? 'opacity-100' : 'opacity-0'}`} size={18} />
                  Masculin
                </label>
                
                <label className={`flex items-center p-2.5 border rounded-lg cursor-pointer ${
                  formData.sexe === 'F' 
                    ? 'bg-[#eef7fb] border-[#246990] text-[#246990]' 
                    : 'bg-white border-gray-200 text-gray-700'
                }`}>
                  <input
                    type="radio"
                    name="sexe"
                    value="F"
                    checked={formData.sexe === 'F'}
                    onChange={handleInputChange}
                    className="hidden"
                    required
                  />
                  <Check className={`mr-2 ${formData.sexe === 'F' ? 'opacity-100' : 'opacity-0'}`} size={18} />
                  Féminin
                </label>
                
                <label className={`flex items-center p-2.5 border rounded-lg cursor-pointer ${
                  formData.sexe && formData.sexe !== 'M' && formData.sexe !== 'F'
                    ? 'bg-[#eef7fb] border-[#246990] text-[#246990]' 
                    : 'bg-white border-gray-200 text-gray-700'
                }`}>
                  <input
                    type="radio"
                    name="sexe"
                    value="Autre"
                    checked={formData.sexe && formData.sexe !== 'M' && formData.sexe !== 'F'}
                    onChange={handleInputChange}
                    className="hidden"
                    required
                  />
                  <Check className={`mr-2 ${formData.sexe && formData.sexe !== 'M' && formData.sexe !== 'F' ? 'opacity-100' : 'opacity-0'}`} size={18} />
                  Autre
                </label>
              </div>
              
              {/* Champ pour préciser si "Autre" est sélectionné */}
              {formData.sexe && formData.sexe !== 'M' && formData.sexe !== 'F' && (
                <div className="mt-2">
                  <input
                    type="text"
                    name="sexe_autre"
                    value={formData.sexe_autre || ''}
                    onChange={handleInputChange}
                    placeholder="Veuillez préciser"
                    className="w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                    required
                  />
                </div>
              )}
            </div>
            
            {/* Date de naissance */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Date de naissance <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="date"
                  name="date_naissance"
                  value={formData.date_naissance}
                  onChange={handleInputChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
            
            {/* Lieu de naissance (Nouveau champ) */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Lieu de naissance <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  name="lieu_naissance"
                  value={formData.lieu_naissance || ''}
                  onChange={handleInputChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
            
            {/* Nationalité */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Nationalité <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={18} />
                <Select
                  name="nationalite"
                  value={
                    formData.nationalite
                      ? { value: formData.nationalite, label: formData.nationalite }
                      : null
                  }
                  onChange={(selectedOption) => {
                    setFormData(prev => ({
                      ...prev,
                      nationalite: selectedOption ? selectedOption.value : ''
                    }));
                  }}
                  options={countryOptions}
                  className="country-select"
                  classNamePrefix="country-select"
                  placeholder="Sélectionnez une nationalité"
                  styles={{
                    control: (base) => ({
                      ...base,
                      paddingLeft: '26px',
                      minHeight: '42px',
                      borderColor: '#e5e7eb',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#e5e7eb'
                      }
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '2px 8px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontSize: '0.875rem'
                    })
                  }}
                  required
                />
              </div>
            </div>
            
            {/* Email */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Email
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                {"Ce champ est facultatif si l'employ\u00e9 n'a pas d'adresse email."}
              </p>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  placeholder="adresse@email.com"
                />
              </div>
            </div>
                        {/* Téléphones */}
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Téléphones <span className="text-red-500">*</span>
                </label>
                <button type="button" onClick={addEmployeePhone} className="inline-flex items-center gap-1.5 rounded-lg border border-[#b8d4e2] bg-[#e7f3f8] px-3 py-1.5 text-xs font-medium text-[#246990] hover:bg-[#d9edf5]">
                  <Plus size={14} />
                  Ajouter un numéro
                </button>
              </div>
              <div className="space-y-3">
                {getPhoneFields().map((phone, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="tel"
                        name={`telephone_${index}`}
                        value={phone}
                        onChange={(e) => handleEmployeePhoneChange(index, e.target.value)}
                        className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                        placeholder={index === 0 ? 'Numéro principal' : `Numéro secondaire ${index}`}
                        required={index === 0}
                      />
                    </div>
                    {index > 0 && (
                      <button type="button" onClick={() => removeEmployeePhone(index)} className="p-2.5 rounded-lg text-red-500 hover:bg-red-50" title="Retirer ce numéro">
                        <X size={18} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
        
        {/* Adresse - Version améliorée (Déplacée avant Informations professionnelles) */}
        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5">
            <div className="p-2 bg-[#246990] rounded-full mr-4">
              <MapPin className="text-white" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-[#246990] text-lg">Adresse</h3>
              <p className="text-sm text-gray-600">Adresse de résidence de l'employé.</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Rue et numéro */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Rue et numéro <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
                <textarea
                  name="adresse"
                  value={formData.adresse || ''}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Ville */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Ville <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="ville"
                    value={formData.ville || ''}
                    onChange={handleInputChange}
                    className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                    required
                  />
                </div>
              </div>
              
              {/* Code postal */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Code postal <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="code_postal"
                    value={formData.code_postal || ''}
                    onChange={handleInputChange}
                    className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                    required
                  />
                </div>
              </div>
              
              {/* Pays */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Pays <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={18} />
                  <Select
                    name="pays"
                    value={
                      formData.pays
                        ? { value: formData.pays, label: formData.pays }
                        : null
                    }
                    onChange={(selectedOption) => {
                      setFormData(prev => ({
                        ...prev,
                        pays: selectedOption ? selectedOption.value : ''
                      }));
                    }}
                    options={countryOptions}
                    className="country-select"
                    classNamePrefix="country-select"
                    placeholder="Sélectionnez un pays"
                    styles={{
                      control: (base) => ({
                        ...base,
                        paddingLeft: '26px',
                        minHeight: '42px',
                        borderColor: '#e5e7eb',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#e5e7eb'
                        }
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '2px 8px'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        fontSize: '0.875rem'
                      })
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* SECTION: Informations professionnelles - Version améliorée */}
        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5">
            <div className="p-2 bg-[#246990] rounded-full mr-4">
              <Briefcase className="text-white" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-[#246990] text-lg">Informations professionnelles</h3>
              <p className="text-sm text-gray-600">Détails concernant le poste et le contrat de travail.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            {/* Date d'embauche */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Date d'embauche <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="date"
                  name="date_embauche"
                  value={formData.info_pro?.date_embauche || formData.date_embauche || ''}
                  onChange={handleInfoProChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
            
            {/* Type de contrat pour badges/salaires */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Type de contrat <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
          
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      type_contrat: 'FIXE',
                      info_pro: { 
                        ...prev.info_pro, 
                        type_contrat: 'FIXE' // ✅ Synchronisé
                      }
                    }))}
                    className={`flex items-center px-4 py-2.5 rounded-md border transition-colors shadow-sm ${
                      (formData.type_contrat === 'FIXE' || formData.info_pro?.type_contrat === 'FIXE')
                        ? 'bg-[#246990] text-white border-[#246990]'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Calendar className="mr-2" size={18} />
                    Fixe
                    {(formData.type_contrat === 'FIXE' || formData.info_pro?.type_contrat === 'FIXE') && (
                      <Check className="ml-2" size={16} />
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      type_contrat: 'HORAIRE',
                      info_pro: { 
                        ...prev.info_pro, 
                        type_contrat: 'HORAIRE' // ✅ Synchronisé
                      }
                    }))}
                    className={`flex items-center px-4 py-2.5 rounded-md border transition-colors shadow-sm ${
                      (formData.type_contrat === 'HORAIRE' || formData.info_pro?.type_contrat === 'HORAIRE')
                        ? 'bg-[#246990] text-white border-[#246990]'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Clock className="mr-2" size={18} />
                    Horaire
                    {(formData.type_contrat === 'HORAIRE' || formData.info_pro?.type_contrat === 'HORAIRE') && (
                      <Check className="ml-2" size={16} />
                    )}
                  </button>
              </div>


              
              <div className="mt-3 p-3 bg-[#eef7fb] rounded-lg border border-[#246990]/20 rounded-md">
                <p className="text-sm text-gray-600">
                  <strong>{(formData.type_contrat || formData.info_pro?.type_contrat) === 'HORAIRE' ? 'HORAIRE' : 'FIXE'} :</strong> {' '}
                  {(formData.type_contrat || formData.info_pro?.type_contrat) === 'HORAIRE' 
                    ? 'Salaire calculé selon les heures de présence validées par classe enseignée.'
                    : 'Salaire mensuel fixe ajusté selon le ratio de présence journalière.'
                  }
                </p>
              </div>
            </div>

            {/* Statut légal du contrat */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Statut légal <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FormDropdown
                  value={formData.info_pro?.statut_legal || ''}
                  options={statutLegalOptions}
                  placeholder="Sélectionnez un statut"
                  icon={Briefcase}
                  onChange={(value) => {
                    setFormData(prev => ({
                      ...prev,
                      info_pro: {
                        ...(prev.info_pro || {}),
                        statut_legal: value
                      }
                    }));
                  }}
                />
              </div>
            </div>


          </div>
          
          {/* Date de fin de contrat (uniquement pour CDD) */}
          {formData.info_pro?.type_contrat === 'CDD' && (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Date de fin de contrat <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="date"
                  name="date_fin_contrat"
                  value={formData.info_pro?.date_fin_contrat || ''}
                  onChange={handleInfoProChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
              {formData.info_pro?.date_embauche && formData.info_pro?.date_fin_contrat && (
                <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-700 flex items-center">
                  <AlertCircle className="text-blue-600 mr-2" size={14} />
                  Durée du contrat: {calculateContractDuration(formData.info_pro?.date_embauche, formData.info_pro?.date_fin_contrat)}
                </div>
              )}
            </div>
          )}
          
          {/* Précision si "Autre" type de contrat */}
          {formData.info_pro?.type_contrat === 'Autre' && (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Précisez le type de contrat <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  name="type_contrat_autre"
                  value={formData.info_pro?.type_contrat_autre || ''}
                  onChange={handleInfoProChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
          )}

          {/* Le num\u00e9ro de badge ne sert qu'au pointage : sans ce module, le
              champ n'aurait aucun usage. */}
          {pointageOuvert && (
          <div>
          <label className="text-sm font-medium text-gray-700 block mb-1.5">
            {"Num\u00e9ro de badge"}
          </label>
          <div className="relative">
            <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              name="numero_badge"
              value={formData.numero_badge}
              onChange={handleInputChange}
              placeholder="BD00123"

              className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {"Facultatif. Format sugg\u00e9r\u00e9 : BD + 5 chiffres (ex: BD00123)."}
          </p>
        </div>
          )}


        {/* BADGE RFID */}
        {rfidOuvert && (
        <div className="sm:col-span-2">
          <label className="text-sm font-medium text-gray-700 block mb-1.5">
            Badge RFID (scan automatique)
          </label>
          
          <div className="relative">
            {/* Champ caché pour capture RFID */}
            <input
              type="text"
              value=""
              onChange={() => {}}
              onKeyDown={handleRfidKeyDown}
              onFocus={(e) => {
                setRfidFieldActive(true);
                e.target.style.borderColor = '#246990';
                e.target.style.boxShadow = '0 0 0 3px rgba(36, 105, 144, 0.1)';
              }}
              onBlur={(e) => {
                setRfidFieldActive(false);
                e.target.style.borderColor = '#e5e5e5';
                e.target.style.boxShadow = 'none';
              }}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
              placeholder="Cliquez ici et scannez le badge RFID"
            />
            
            {/* Champ d'affichage (non modifiable) avec feedback visuel */}
            <div
              className="w-full px-4 py-2.5 border-2 rounded-lg font-bold text-base transition-all duration-200"
              style={{ 
                backgroundColor: rfidFieldActive ? '#eef7fb' : (formData.rfid_uid ? '#eef7fb' : '#f9fafb'),
                borderColor: rfidFieldActive ? '#246990' : (formData.rfid_uid ? '#246990' : '#e5e7eb'),
                color: formData.rfid_uid ? '#1f2937' : '#9ca3af',
                cursor: 'pointer',
                boxShadow: rfidFieldActive ? '0 0 0 3px rgba(36, 105, 144, 0.1)' : 'none'
              }}
              onClick={() => {
                // Focus sur le champ caché
                const hiddenInput = document.querySelector('input[placeholder="Cliquez ici et scannez le badge RFID"]');
                if (hiddenInput) hiddenInput.focus();
              }}
            >
              <div className="flex items-center justify-between">
                <span>
                  {formData.rfid_uid || 'En attente du scan RFID...'}
                </span>
                
                {/* Indicateurs visuels */}
                {rfidFieldActive && !formData.rfid_uid && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#246990] animate-pulse"></div>
                    <span className="text-xs font-semibold text-[#246990]">Prêt à scanner</span>
                  </div>
                )}
                
                {rfidCapture && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-xs font-semibold text-green-600">Lecture en cours...</span>
                  </div>
                )}
                
                {formData.rfid_uid && !rfidFieldActive && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-xs font-semibold text-green-600">✓ Capturé</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            {rfidFieldActive ? 'Champ actif - Approchez votre badge du lecteur' : 'Cliquez sur le champ et approchez votre badge RFID du lecteur'}
          </p>
          
          {/* Bouton pour effacer si besoin */}
          {formData.rfid_uid && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFormData(prev => ({ ...prev, rfid_uid: '' }));
                setToastInfo({
                  message: 'Badge RFID effacé',
                  type: 'info'
                });
              }}
              className="mt-2 text-xs font-semibold text-[#246990] hover:underline"
            >
              Effacer le badge RFID
            </button>
          )}
        </div>
        )}
       
        

          {/* Accès à l'application */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <label className="text-sm font-medium text-gray-700 block mb-3">
              L'employé aura-t-il accès à l'application ? <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => handleInfoProChange({
                  target: { name: 'acces_application', type: 'checkbox', checked: true }
                })}
                className={`px-5 py-2.5 rounded-lg flex items-center shadow-sm transition-colors ${
                  formData.info_pro?.acces_application 
                    ? 'bg-[#246990] text-white' 
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                <Check className={`mr-2 ${formData.info_pro?.acces_application ? 'opacity-100' : 'opacity-0'}`} size={18} />
                Oui
              </button>
              <button
                type="button"
                onClick={() => handleInfoProChange({
                  target: { name: 'acces_application', type: 'checkbox', checked: false }
                })}
                className={`px-5 py-2.5 rounded-lg flex items-center shadow-sm transition-colors ${
                  formData.info_pro?.acces_application === false
                    ? 'bg-[#c66d59] text-white' 
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                <X className={`mr-2 ${formData.info_pro?.acces_application === false ? 'opacity-100' : 'opacity-0'}`} size={18} />
                Non
              </button>
            </div>
            {formData.info_pro?.acces_application && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start space-x-3">
                <div className="p-1 bg-blue-100 rounded-full mt-0.5">
                  <AlertCircle className="text-blue-600" size={16} />
                </div>
                <p className="text-sm text-blue-700">
                  Des identifiants de connexion seront générés automatiquement pour cet employé. 
                  Après la soumission du formulaire, la demande d'accès à l'application sera envoyée 
                  à la direction pour validation.
                </p>
              </div>
            )}
          </div>

        </div>
        
        {/* Contact d'urgence - Version améliorée */}
        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5">
            <div className="p-2 bg-[#246990] rounded-full mr-4">
              <Phone className="text-white" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-[#246990] text-lg">Contact d'urgence</h3>
              <p className="text-sm text-gray-600">Personne à contacter en cas d'urgence.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            {/* Nom et prénom */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Nom et prénom <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  name="nom_prenom"
                  value={formData.contact_urgence.nom_prenom || ''}
                  onChange={handleContactUrgenceChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
            
            {/* Relation */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Relation <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FormDropdown
                  value={formData.contact_urgence.relation || ''}
                  options={contactRelationOptions}
                  placeholder="Sélectionnez une relation"
                  icon={User}
                  onChange={(value) => {
                    setFormData(prev => ({
                      ...prev,
                      contact_urgence: {
                        ...prev.contact_urgence,
                        relation: value
                      }
                    }));
                  }}
                />
              </div>
            </div>
            
            {/* Téléphone */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Téléphone <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="tel"
                  name="telephone"
                  value={formData.contact_urgence.telephone || ''}
                  onChange={handleContactUrgenceChange}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-shadow-sm"
                required
              />
            </div>
          </div>
          
          {/* Email */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                name="email"
                value={formData.contact_urgence.email || ''}
                onChange={handleContactUrgenceChange}
                className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {"Facultatif si le contact d'urgence n'a pas d'adresse email."}
            </p>
          </div>
        </div>
        
        {/* Option pour adresse identique */}
        <div className="mt-5 pt-3 border-t border-gray-100">
          <label className="text-sm font-medium text-gray-700 block mb-3">
            L'adresse du contact d'urgence est-elle identique à celle de l'employé ? <span className="text-red-500">*</span>
          </label>
          <div className="flex space-x-4 mb-4">
            <button
              type="button"
              onClick={() => {
                // Copier l'adresse de l'employé dans le contact d'urgence
                setFormData(prev => ({
                  ...prev,
                  contact_urgence: {
                    ...prev.contact_urgence,
                    adresse: prev.adresse,
                    ville: prev.ville,
                    code_postal: prev.code_postal,
                    pays: prev.pays
                  }
                }));
              }}
              className="px-5 py-2.5 rounded-lg flex items-center shadow-sm bg-[#246990] text-white"
            >
              <Check className="mr-2" size={18} />
              Oui, utiliser la même adresse
            </button>
            <button
              type="button"
              onClick={() => {
                // Vider l'adresse du contact d'urgence si elle était déjà remplie avec celle de l'employé
                if (formData.contact_urgence.adresse === formData.adresse) {
                  setFormData(prev => ({
                    ...prev,
                    contact_urgence: {
                      ...prev.contact_urgence,
                      adresse: '',
                      ville: '',
                      code_postal: '',
                      pays: ''
                    }
                  }));
                }
              }}
              className="px-5 py-2.5 rounded-lg flex items-center shadow-sm bg-white text-gray-700 border border-gray-300"
            >
              <X className="mr-2" size={18} />
              Non, saisir une adresse différente
            </button>
          </div>
          
          {/* Adresse du contact d'urgence */}
          <div className="space-y-4 mt-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Adresse <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
                <textarea
                  name="adresse"
                  value={formData.contact_urgence.adresse || ''}
                  onChange={handleContactUrgenceChange}
                  rows={2}
                  className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Ville <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="ville"
                    value={formData.contact_urgence.ville || ''}
                    onChange={handleContactUrgenceChange}
                    className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Code postal <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="code_postal"
                    value={formData.contact_urgence.code_postal || ''}
                    onChange={handleContactUrgenceChange}
                    className="w-full pl-9 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Pays <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={18} />
                  <Select
                    name="pays"
                    value={
                      formData.contact_urgence.pays
                        ? { value: formData.contact_urgence.pays, label: formData.contact_urgence.pays }
                        : null
                    }
                    onChange={(selectedOption) => {
                      setFormData(prev => ({
                        ...prev,
                        contact_urgence: {
                          ...prev.contact_urgence,
                          pays: selectedOption ? selectedOption.value : ''
                        }
                      }));
                    }}
                    options={countryOptions}
                    className="country-select"
                    classNamePrefix="country-select"
                    placeholder="Sélectionnez un pays"
                    styles={{
                      control: (base) => ({
                        ...base,
                        paddingLeft: '26px',
                        minHeight: '42px',
                        borderColor: '#e5e7eb',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#e5e7eb'
                        }
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '2px 8px'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        fontSize: '0.875rem'
                      })
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Message informatif */}
      <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mt-6">
        <div className="p-2 bg-[#246990] rounded-full mr-4">
          <AlertCircle className="text-white" size={20} />
        </div>
        <div className="flex-1">
          <h5 className="font-medium text-[#246990]">Informations importantes</h5>
          <p className="text-sm text-gray-600">Veuillez remplir tous les champs obligatoires marqués par un astérisque (*) pour passer à l'étape suivante.</p>
        </div>
      </div>
    </div>
  );
};





  // Ajoutez cette fonction dans votre composant
const handleRoleSelection = (index, roleId, roleName) => {
  const updatedRoles = [...formData.roles];
  const roleInfo = updatedRoles[index];
  
  // Initialiser le tableau des rôles sélectionnés s'il n'existe pas
  if (!roleInfo.roles_selectionnes) {
    roleInfo.roles_selectionnes = [];
  }
  
  // Vérifier si le rôle est déjà sélectionné
  const isSelected = roleInfo.roles_selectionnes.some(r => r.role_id === roleId);
  
  if (isSelected) {
    // Si déjà sélectionné, on le retire
    roleInfo.roles_selectionnes = roleInfo.roles_selectionnes.filter(r => r.role_id !== roleId);
  } else {
    // Sinon, on l'ajoute
    roleInfo.roles_selectionnes.push({
      role_id: roleId,
      role_nom: roleName
    });
  }
  
  setFormData(prev => ({ ...prev, roles: updatedRoles }));
};
  
  
const renderStep2 = () => {
  // Définition de la correspondance entre groupes et rôles
  const groupeRolesMapping = {
    '1': [
      { id: 1, nom: 'Super Admin' },
      { id: 2, nom: 'Développeur' }
    ],
    '2': [
      { id: 3, nom: 'Promoteur' },
      { id: 4, nom: 'Directeur(trice)' },
      { id: 5, nom: 'Sous-directeur' }
    ],
    '3': [
      { id: 6, nom: 'Comptable' },
      { id: 7, nom: 'Secrétaire' },
      { id: 8, nom: 'Gestionnaire pédagogique' }
    ],
    '4': [
      { id: 9, nom: 'Enseignants de l\'établissement' }
    ],
    '5': [
      { id: 10, nom: 'Personnel de surveillance' }
    ],
    '6': [
      { id: 11, nom: 'Personnel technique' }
    ]
  };

  return (
    <div className="space-y-6">
      {renderError()}
      
      {formData.roles.map((role, index) => (
        <div key={role.id} className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-6">
          {/* En-tête Rôle principal/additionnel avec style amélioré */}
          <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center justify-between mb-5">
            <div className="flex items-center">
              <div className="p-2 bg-[#246990] rounded-full mr-4">
                <Users className="text-white" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-[#246990] text-lg">
                  {index === 0 ? "Rôle principal" : `Rôle additionnel ${index}`}
                </h3>
                <p className="text-sm text-gray-600">
                  {index === 0 ? "Configuration du rôle principal de l'employé" : "Configuration d'un rôle supplémentaire"}
                </p>
              </div>
            </div>
            
            {index > 0 && (
              <button
                type="button"
                onClick={() => handleRemoveRole(index)}
                className="text-red-600 hover:text-red-800 text-sm flex items-center"
              >
                <X size={16} className="mr-1" />
                Supprimer
              </button>
            )}
          </div>
          
          {/* Section des droits d'accès */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="text-[#246990]" size={18} />
              <h4 className="text-base font-medium text-gray-800">Gestion des droits d'accès</h4>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Liste des groupes de sécurité */}
              <div className="lg:col-span-1 border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 bg-white">
                  <div className="flex items-center mb-2">
                    
                    <h5 className="text-sm font-medium">Groupe de sécurité</h5>
                  </div>
                  <p className="text-xs text-gray-500">Configurez les droits pour chaque rôle</p>
                </div>
                
                <div className="p-3 space-y-2">
                  {groupesSecurite.map(groupe => (
                    <div 
                      key={groupe.id}
                      className={`p-3 border rounded-md cursor-pointer ${
                        role.groupe_securite_id === groupe.id 
                          ? 'bg-[#246990]/10 border-[#246990]'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleRoleChange(index, 'groupe_securite_id', groupe.id)}
                    >
                      <h3 className="font-medium text-sm">{groupe.nom}</h3>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Liste des rôles disponibles pour le groupe sélectionné */}
              <div className="lg:col-span-2 border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 bg-white">
                  <h5 className="text-sm font-medium">
                    {role.groupe_securite_id 
                      ? `Sélectionnez un rôle`
                      : 'Sélectionnez d\'abord un groupe de sécurité'}
                  </h5>
                  <p className="text-xs text-gray-500">Définissez les actions autorisées pour ce rôle</p>
                </div>
                
                <div className="p-4">
                  {role.groupe_securite_id ? (
                    <div className="space-y-2">
                      {rolesByGroup[role.groupe_securite_id]?.map(roleOption => (
                        <div 
                          key={roleOption.id}
                          className={`p-3 border rounded-md cursor-pointer ${
                            (role.roles_selectionnes || []).some(r => r.role_id === roleOption.id)
                              ? 'bg-[#246990]/10 border-[#246990]'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleRoleSelection(index, roleOption.id, roleOption.nom)}
                        >
                          <h3 className="font-medium text-sm">{roleOption.nom}</h3>
                        </div>
                      ))}
                      {(!rolesByGroup[role.groupe_securite_id] || rolesByGroup[role.groupe_securite_id].length === 0) && (
                        <div className="text-center py-4 text-gray-500">
                          Aucun rôle disponible pour ce groupe de sécurité
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-gray-500">
                      Veuillez sélectionner un groupe de sécurité dans la liste pour voir les rôles disponibles
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mode de rémunération automatique */}
              <div className="mb-5">
                <div className="flex items-center space-x-2 mb-4">
                  <CreditCard className="text-[#246990]" size={18} />
                  <h4 className="text-base font-medium text-gray-800">Mode de rémunération</h4>
                </div>
                
                {/* Mode de rémunération sans les gros cercles */}
                <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {role.mode_remuneration === 'HORAIRE' ? (
                        <>
                          <div className="p-2 bg-[#246990] rounded-lg">
                            <Clock className="text-white" size={18} />
                          </div>
                          <div>
                            <h5 className="font-medium text-gray-900">Rémunération horaire</h5>
                            <p className="text-sm text-gray-600">Salaire basé sur les heures de présence validées</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="p-2 bg-[#246990] rounded-lg">
                            <Calendar className="text-white" size={18} />
                          </div>
                          <div>
                            <h5 className="font-medium text-[#246990] text-lg">Rémunération mensuelle</h5>
                            <p className="text-sm text-gray-600">Salaire mensuel fixe ajusté selon la présence</p>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      (formData.type_contrat === 'HORAIRE' || formData.info_pro?.type_contrat === 'HORAIRE')
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {(formData.type_contrat === 'HORAIRE' || formData.info_pro?.type_contrat === 'HORAIRE') ? 'HORAIRE' : 'FIXE'}
                    </span>
                  </div>
                  
                </div>

                {/* Configuration des taux horaires */}
                {role.mode_remuneration === 'HORAIRE' && role.classes_selectionnees?.length > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                
                      {role.taux_horaires_predefinis?.length > 0 && (
                        <div className="flex items-center text-green-700 text-sm">
                          <CheckCircle className="mr-1" size={14} />
                          Taux pré-remplis automatiquement
                        </div>
                      )}
                    </div>

                    {/* Affichage des taux pré-définis */}
                    {role.taux_horaires_predefinis?.length > 0 ? (
                      <div className="space-y-4">
                        {/* En-tête amélioré */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-green-100 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <h5 className="font-semibold text-green-800">Taux horaires</h5>
                                <p className="text-sm text-green-700">Récupérés depuis la configuration des classes</p>
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-green-600 bg-green-100 px-3 py-1 rounded-full inline-block">
                            💡 Modification possible via la gestion des classes uniquement
                          </div>
                        </div>

                        {/* Cartes des classes avec design amélioré */}
                        <div className="grid gap-4">
                          {role.taux_horaires_predefinis.map((tauxInfo, tauxIndex) => (
                            <div key={tauxInfo.classe_id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                              {/* En-tête de la classe */}
                              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                                <div className="flex items-center space-x-3">
                                  <div className="p-2 bg-[#246990] rounded-lg">
                                    <BookOpen className="w-4 h-4 text-white" />
                                  </div>
                                  <div>
                                    <h6 className="font-semibold text-gray-900">{tauxInfo.classe_nom}</h6>
                                    <p className="text-xs text-gray-500">Classe enseignée</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-[#246990]">
                                    {formatMontant(tauxInfo.taux_horaire)}
                                  </div>
                                  <div className="text-xs text-gray-500">par heure</div>
                                </div>
                              </div>

                              {/* Contenu de la classe */}
                              <div className="p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Taux horaire (lecture seule amélioré) */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      <div className="flex items-center space-x-2">
                                        <span>Taux horaire configuré</span>
                                        <div className="flex items-center space-x-1">
                                          <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                                          <span className="text-xs text-red-600">non modifiable</span>
                                        </div>
                                      </div>
                                    </label>
                                    <div className="relative">
                                      <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-semibold flex items-center justify-between">
                                        <span>{formatMontant(tauxInfo.taux_horaire)} / heure</span>
                                        <div className="flex items-center space-x-1 text-green-600">
                                          <CheckCircle className="w-4 h-4" />
                                          <span className="text-xs">Validé</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Heures par semaine (modifiable amélioré) */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      <div className="flex items-center space-x-2">
                                        <span>Heures par semaine</span>
                                        <div className="flex items-center space-x-1">
                                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                          <span className="text-xs text-green-600">Modifiable</span>
                                        </div>
                                      </div>
                                    </label>
                                    <div className="relative">
                                      <input
                                        type="number"
                                        value={tauxInfo.heures_semaine}
                                        onChange={(e) => {
                                          const newTaux = [...(role.taux_horaires_predefinis || [])];
                                          newTaux[tauxIndex] = {
                                            ...newTaux[tauxIndex],
                                            heures_semaine: parseFloat(e.target.value) || 0
                                          };
                                          handleRoleChange(index, 'taux_horaires_predefinis', newTaux);
                                        }}
                                        min="0"
                                        max="40"
                                        step="0.5"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent transition-colors"
                                        placeholder="Ex: 15"
                                      />
                                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                                        h/sem
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Calcul en temps réel */}
                                <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <div className="p-1 bg-blue-100 rounded">
                                        <Calculator className="w-3 h-3 text-blue-600" />
                                      </div>
                                      <span className="text-sm font-medium text-blue-800">Remunération mensuelle</span>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-lg font-bold text-blue-900">
                                        {formatMontant((tauxInfo.taux_horaire || 0) * (tauxInfo.heures_semaine || 0) * 4)}
                                      </div>
                                      <div className="text-xs text-blue-600">
                                        {tauxInfo.heures_semaine || 0}h × 4 semaines
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Total global amélioré */}
                        <div className="bg-[#246990] text-white rounded-lg p-4 shadow-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div>
                                <h5 className="font-semibold">Rémunération totale</h5>
                                <p className="text-sm text-blue-100">Basée sur {role.taux_horaires_predefinis?.length || 0} classe(s)</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold">
                                {formatMontant(
                                  (role.taux_horaires_predefinis || []).reduce((total, taux) => 
                                    total + ((taux.taux_horaire || 0) * (taux.heures_semaine || 0) * 4), 0
                                  )
                                )}
                              </div>
                              <div className="text-sm text-blue-100">par mois</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Affichage par défaut si pas de taux pré-définis */
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-center text-yellow-800 text-sm">
                          <AlertTriangle className="mr-2" size={14} />
                          <span>Aucun taux horaire pré-défini trouvé pour les classes sélectionnées.</span>
                        </div>
                        <p className="text-yellow-700 text-xs mt-1">
                          Vous devrez configurer manuellement les taux horaires pour chaque classe.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Option pour ajouter un rôle avec un mode différent */}
                {index > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-3">Pour ce rôle additionnel, vous pouvez choisir un mode différent :</p>
                    <div className="flex flex-wrap space-x-4">
                      <button
                        type="button"
                        onClick={() => handleRoleChange(index, 'mode_remuneration', 'MENSUEL')}
                        className={`flex items-center px-4 py-2.5 rounded-md border transition-colors mb-2 shadow-sm ${
                          role.mode_remuneration === 'MENSUEL'
                            ? 'bg-[#246990] text-white border-[#246990]'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Calendar className="mr-2" size={18} />
                        Mensuel
                        {role.mode_remuneration === 'MENSUEL' && (
                          <Check className="ml-2" size={16} />
                        )}
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => handleRoleChange(index, 'mode_remuneration', 'HORAIRE')}
                        className={`flex items-center px-4 py-2.5 rounded-md border transition-colors mb-2 shadow-sm ${
                          role.mode_remuneration === 'HORAIRE'
                            ? 'bg-[#246990] text-white border-[#246990]'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Clock className="mr-2" size={18} />
                        Horaire
                        {role.mode_remuneration === 'HORAIRE' && (
                          <Check className="ml-2" size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
                        

          {/* Partie spécifique au mode MENSUEL */}
          {role.mode_remuneration === 'MENSUEL' && (
            <div className="mt-4">
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                {/* En-tête Salaire mensuel de base */}
                <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-gray-100">
                  <h4 className="text-base font-medium text-gray-800">Salaire mensuel de base</h4>
                </div>
                
                <div>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={role.salaire_base}
                      onChange={(e) => handleRoleChange(index, 'salaire_base', e.target.value)}
                      className="w-full pl-10 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                      placeholder="Montant"
                    />
                  </div>
                </div>
                
                {/* Section de choix pour les primes */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="p-1 bg-[#eef7fb] rounded-full">
                        <Plus className="text-[#246990]" size={16} />
                      </div>
                      <label className="text-sm font-medium text-gray-700">Le salarié aura-t-il des primes ?</label>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => handleHasBonusChange(index, 'a_primes', true)}
                        className={`px-4 py-1.5 rounded-md transition-colors shadow-sm ${
                          role.a_primes 
                            ? 'bg-[#246990] text-white' 
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHasBonusChange(index, 'a_primes', false)}
                        className={`px-4 py-1.5 rounded-md transition-colors shadow-sm ${
                          role.a_primes === false 
                            ? 'bg-[#246990] text-white' 
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Non
                      </button>
                    </div>
                  </div>
                  
                  {/* Affichage des primes uniquement si "oui" est sélectionné */}
                  {role.a_primes && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Ligne 1: Indemnité de responsabilité + Prime de transport */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Indemnité de responsabilité</span>
                          <div className="relative">
                            <input
                              type="text"
                              value={role.primes.indemnite_responsabilite}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'indemnite_responsabilite')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Prime de transport</span>
                          <div className="relative">
                       
                            <input
                              type="text"
                              value={role.primes.prime_transport}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'prime_transport')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Ligne 2: Prime d'encouragement + Prime d'ancienneté */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Prime d'encouragement</span>
                          <div className="relative">
                           
                            <input
                              type="text"
                              value={role.primes.prime_encouragement}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'prime_encouragement')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Prime d'ancienneté</span>
                          <div className="relative">
                          
                            <input
                              type="text"
                              value={role.primes.prime_anciennete}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'prime_anciennete')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Ligne 3: Indemnité de soins médicaux */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Indemnité de soins médicaux</span>
                          <div className="relative">
                       
                            <input
                              type="text"
                              value={role.primes.indemnite_medicale}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'indemnite_medicale')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Section de choix pour les retenues */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="p-1 bg-[#eef7fb] rounded-full">
                        <Minus className="text-[#246990]" size={16} />
                      </div>
                      <label className="text-sm font-medium text-gray-700">Le salarié aura-t-il des retenues sur salaire ?</label>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => handleHasBonusChange(index, 'a_retenues', true)}
                        className={`px-4 py-1.5 rounded-md transition-colors shadow-sm ${
                          role.a_retenues 
                            ? 'bg-[#246990] text-white' 
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHasBonusChange(index, 'a_retenues', false)}
                        className={`px-4 py-1.5 rounded-md transition-colors shadow-sm ${
                          role.a_retenues === false 
                            ? 'bg-[#246990] text-white' 
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Non
                      </button>
                    </div>
                  </div>
                  
                  {/* Affichage des retenues uniquement si "oui" est sélectionné */}
                  {role.a_retenues && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* CNSS */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">CNSS</span>
                          <div className="relative">
                           
                            <input
                              type="text"
                              value={role.retenues.cnss}
                              onChange={(e) => handleRemunerationChange(e, index, 'retenues', 'cnss')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Assurance */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Assurance</span>
                          <div className="relative">
                            
                            <input
                              type="text"
                              value={role.retenues.assurance}
                              onChange={(e) => handleRemunerationChange(e, index, 'retenues', 'assurance')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Total */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg shadow-sm">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Rémunération totale:</span>
                    <span className="font-bold text-lg text-[#246990]">
                      {(() => {
                        const base = parseFloat(role.salaire_base) || 0;
                        const primes = role.a_primes ? 
                          Object.values(role.primes).reduce(
                            (sum, val) => sum + (parseFloat(val) || 0),
                            0
                          ) : 0;
                        const retenues = role.a_retenues ? 
                          Object.values(role.retenues).reduce(
                            (sum, val) => sum + (parseFloat(val) || 0),
                            0
                          ) : 0;
                        
                        const total = base + primes - retenues;
                        return formatMontant(total);
                      })()}
                    </span>
  
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Partie spécifique au mode HORAIRE */}
          {role.mode_remuneration === 'HORAIRE' && (
            <div className="mt-4 space-y-4">
              {/* Sélection des classes avec en-tête amélioré */}


              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-gray-100">
                <BookOpen className="text-[#246990]" size={18} />
                <h4 className="text-base font-medium text-gray-800">Classes enseignées</h4>
              </div>
              
              {(() => {
                // Grouper les classes par section
                const classesBySection = classes.reduce((acc, classe) => {
                  const sectionId = classe.section_id || 'sans_section';
                  if (!acc[sectionId]) {
                    acc[sectionId] = [];
                  }
                  acc[sectionId].push(classe);
                  return acc;
                }, {});

                // Ajouter les sections manquantes
                const allSections = [...sections];
                if (classesBySection.sans_section) {
                  allSections.push({ id: 'sans_section', nom: 'Sans section', ordre: 999 });
                }

                // Trier les sections par ordre
                const sortedSections = allSections.sort((a, b) => {
                  if (a.ordre && b.ordre) return a.ordre - b.ordre;
                  if (a.ordre) return -1;
                  if (b.ordre) return 1;
                  return a.nom.localeCompare(b.nom);
                });

                // Fonctions helper
                const getSectionIcon = (sectionName) => {
                  const name = sectionName.toLowerCase();
                  if (name.includes('préscolaire') || name.includes('prescolaire') || name.includes('maternelle')) {
                    return <Baby className="w-4 h-4" />;
                  }
                  if (name.includes('primaire') || name.includes('élémentaire')) {
                    return <BookOpen className="w-4 h-4" />;
                  }
                  if (name.includes('collège') || name.includes('college')) {
                    return <School className="w-4 h-4" />;
                  }
                  if (name.includes('lycée') || name.includes('lycee') || name.includes('secondaire')) {
                    return <Building className="w-4 h-4" />;
                  }
                  return <GraduationCap className="w-4 h-4" />;
                };

                const isSectionFullySelected = (sectionId) => {
                  const sectionClasses = classesBySection[sectionId] || [];
                  return sectionClasses.length > 0 && 
                        sectionClasses.every(classe => role.classes_selectionnees?.includes(classe.id));
                };

                const isSectionPartiallySelected = (sectionId) => {
                  const sectionClasses = classesBySection[sectionId] || [];
                  return sectionClasses.some(classe => role.classes_selectionnees?.includes(classe.id)) &&
                        !isSectionFullySelected(sectionId);
                };

                const toggleSection = (sectionId) => {
                  const sectionClasses = classesBySection[sectionId] || [];
                  const sectionClassIds = sectionClasses.map(c => c.id);
                  const updatedRoles = [...formData.roles];
                  
                  if (isSectionFullySelected(sectionId)) {
                    // Désélectionner toute la section
                    updatedRoles[index].classes_selectionnees = (updatedRoles[index].classes_selectionnees || []).filter(
                      id => !sectionClassIds.includes(id)
                    );
                  } else {
                    // Sélectionner toute la section
                    const currentSelection = updatedRoles[index].classes_selectionnees || [];
                    sectionClassIds.forEach(id => {
                      if (!currentSelection.includes(id)) {
                        currentSelection.push(id);
                      }
                    });
                    updatedRoles[index].classes_selectionnees = currentSelection;
                  }
                  
                  setFormData(prev => ({ ...prev, roles: updatedRoles }));

                  // Auto-remplissage pour mode HORAIRE
                  if (role.mode_remuneration === 'HORAIRE') {
                    setTimeout(() => autoFillTauxHoraires(updatedRoles[index].classes_selectionnees, index), 200);
                  }
                };

                return (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {sortedSections.map(section => {
                      const sectionClasses = classesBySection[section.id] || [];
                      const isExpanded = expandedSections.has(section.id);
                      const isFullySelected = isSectionFullySelected(section.id);
                      const isPartiallySelected = isSectionPartiallySelected(section.id);

                      if (sectionClasses.length === 0) return null;

                      return (
                        <div key={section.id} className="border-b border-gray-100 last:border-b-0">
                          {/* En-tête de section */}
                          <div 
                            className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors bg-gray-25"
                            onClick={() => toggleExpansion(section.id)}
                          >
                            <div className="flex items-center space-x-3">
                              {/* Icône d'expansion */}
                              <div className="text-gray-400">
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </div>

                              {/* Icône de section */}
                              <div className="text-[#246990]">
                                {getSectionIcon(section.nom)}
                              </div>

                              {/* Nom de section */}
                              <div>
                                <h4 className="font-medium text-gray-900 text-sm">{section.nom}</h4>
                                <p className="text-xs text-gray-500">
                                  {sectionClasses.length} classe{sectionClasses.length > 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>

                            {/* Checkbox de section */}
                            <div className="flex items-center space-x-2">
                              {isPartiallySelected && (
                                <span className="text-xs text-orange-600 font-medium">
                                  Partielle
                                </span>
                              )}
                              <label 
                                className="flex items-center space-x-2 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={isFullySelected}
                                  ref={input => {
                                    if (input) input.indeterminate = isPartiallySelected;
                                  }}
                                  onChange={() => toggleSection(section.id)}
                                  className="rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                                />
                                <span className="text-xs font-medium text-gray-700">
                                  Tout
                                </span>
                              </label>
                            </div>
                          </div>

                          {/* Classes de la section */}
                          {isExpanded && (
                            <div className="bg-white p-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {sectionClasses
                                  .sort((a, b) => {
                                    if (a.ordre && b.ordre) return a.ordre - b.ordre;
                                    return a.nom.localeCompare(b.nom);
                                  })
                                  .map(classe => {
                                    const isSelected = role.classes_selectionnees?.includes(classe.id);
                                    return (
                                      <label
                                        key={classe.id}
                                        className={`flex items-center space-x-2 p-2 rounded border cursor-pointer transition-all text-sm ${
                                          isSelected 
                                            ? 'border-[#246990] bg-[#246990]/5' 
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => handleClassToggle(index, classe.id)}
                                          className="rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <p className="font-medium text-gray-900 truncate text-sm">
                                            {classe.nom}
                                          </p>
                                          {classe.taux_horaire > 0 && (
                                            <p className="text-xs text-green-600 font-medium">
                                              {formatMontant(classe.taux_horaire)}/h
                                            </p>
                                          )}
                                        </div>
                                        {isSelected && (
                                          <Check className="w-3 h-3 text-[#246990]" />
                                        )}
                                      </label>
                                    );
                                  })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
                          
              {/* Ajout des sections primes et retenues pour le mode horaire */}
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                {/* Section de choix pour les primes */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="p-1 bg-[#eef7fb] rounded-full">
                        <Plus className="text-[#246990]" size={16} />
                      </div>
                      <label className="text-sm font-medium text-gray-700">Le salarié aura-t-il des primes ?</label>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => handleHasBonusChange(index, 'a_primes', true)}
                        className={`px-4 py-1.5 rounded-md transition-colors shadow-sm ${
                          role.a_primes 
                            ? 'bg-[#246990] text-white' 
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHasBonusChange(index, 'a_primes', false)}
                        className={`px-4 py-1.5 rounded-md transition-colors shadow-sm ${
                          role.a_primes === false 
                            ? 'bg-[#246990] text-white' 
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Non
                      </button>
                    </div>
                  </div>
                  
                  {/* Affichage des primes uniquement si "oui" est sélectionné */}
                  {role.a_primes && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Même contenu que pour le mode mensuel */}
                      {/* Ligne 1: Indemnité de responsabilité + Prime de transport */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Indemnité de responsabilité</span>
                          <div className="relative">
                          <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              value={role.primes.indemnite_responsabilite}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'indemnite_responsabilite')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Prime de transport</span>
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              value={role.primes.prime_transport}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'prime_transport')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Ligne 2: Prime d'encouragement + Prime d'ancienneté */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Prime d'encouragement</span>
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              value={role.primes.prime_encouragement}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'prime_encouragement')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Prime d'ancienneté</span>
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              value={role.primes.prime_anciennete}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'prime_anciennete')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Ligne 3: Indemnité de soins médicaux */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Indemnité de soins médicaux</span>
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              value={role.primes.indemnite_medicale}
                              onChange={(e) => handleRemunerationChange(e, index, 'primes', 'indemnite_medicale')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Section de choix pour les retenues */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="p-1 bg-[#eef7fb] rounded-full">
                        <Minus className="text-[#246990]" size={16} />
                      </div>
                      <label className="text-sm font-medium text-gray-700">Le salarié aura-t-il des retenues sur salaire ?</label>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => handleHasBonusChange(index, 'a_retenues', true)}
                        className={`px-4 py-1.5 rounded-md transition-colors shadow-sm ${
                          role.a_retenues 
                            ? 'bg-[#246990] text-white' 
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHasBonusChange(index, 'a_retenues', false)}
                        className={`px-4 py-1.5 rounded-md transition-colors shadow-sm ${
                          role.a_retenues === false 
                            ? 'bg-[#246990] text-white' 
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Non
                      </button>
                    </div>
                  </div>
                  
                  {/* Affichage des retenues uniquement si "oui" est sélectionné */}
                  {role.a_retenues && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* CNSS */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">CNSS</span>
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              value={role.retenues.cnss}
                              onChange={(e) => handleRemunerationChange(e, index, 'retenues', 'cnss')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Assurance */}
                      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 mb-2">Assurance</span>
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              value={role.retenues.assurance}
                              onChange={(e) => handleRemunerationChange(e, index, 'retenues', 'assurance')}
                              className="w-full pl-8 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Estimation de la rémunération détaillé*/}

              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
  <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
    <div className="flex items-center space-x-2">
      <Calculator className="text-[#246990]" size={18} />
      <h4 className="text-base font-medium text-gray-800">Détail des calculs de rémunération</h4>
    </div>
    <div className="flex items-center space-x-4">
     
      
      <button 
        type="button" 
        className="text-[#246990] text-sm flex items-center hover:underline transition-colors"
        onClick={() => {
          const detailsEl = document.getElementById(`estimation-details-${index}`);
          const chevronEl = document.getElementById(`chevron-${index}`);
          if (detailsEl && chevronEl) {
            detailsEl.classList.toggle('hidden');
            chevronEl.classList.toggle('rotate-180');
          }
        }}
      >
        <Eye className="mr-1" size={16} />
        Voir détails
        <ChevronDown 
          id={`chevron-${index}`}
          className="ml-1 transition-transform duration-200" 
          size={16} 
        />
      </button>
    </div>
  </div>
  
  <div  className="font-medium text-[#246990] text-lg">
    <p className="text-sm text-gray-600">
      <strong>Analyse complète :</strong> Visualisez en détail la répartition de la rémunération 
      par classe, avec les calculs hebdomadaires et mensuels.
    </p>
  </div>


  {/* Détails collapsibles */}
  <div id={`estimation-details-${index}`} className="mt-4 hidden">
    {role.mode_remuneration === 'HORAIRE' && role.taux_horaires_predefinis?.length > 0 ? (
      <div className="space-y-6">
        {/* Classes sélectionnées avec leurs détails */}
        <div className="grid gap-4">
          {role.taux_horaires_predefinis.map((tauxInfo, classIndex) => {
            const tauxHoraire = tauxInfo.taux_horaire || 0;
            const heuresParSemaine = tauxInfo.heures_semaine || 0;
            
            // Calculs détaillés CORRIGÉS
            const heuresParJour = heuresParSemaine / 5; // 5 jours ouvrés
            const gainParJour = tauxHoraire * heuresParJour;
            const gainParSemaine = tauxHoraire * heuresParSemaine;
            const gainParMois = gainParSemaine * 4; // 4 semaines par mois (cohérent)
            
            return (
              <div key={tauxInfo.classe_id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* En-tête de la classe */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-[#246990] rounded-lg">
                        <BookOpen className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">{tauxInfo.classe_nom}</h5>
                        <p className="text-sm text-gray-600">
                          {formatMontant(tauxHoraire)}/heure × {heuresParSemaine}h/semaine
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-[#246990]">
                        {formatMontant(gainParMois)}
                      </div>
                      <div className="text-xs text-gray-500">par mois</div>
                    </div>
                  </div>
                </div>

                {/* Tableau détaillé des calculs */}
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-medium text-gray-700">PÉRIODE</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-700">HEURES</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-700">TAUX</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-700">MONTANT</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        
                        {/* Par semaine */}
                        <tr className="hover:bg-gray-50">
                          <td className="py-3 px-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-[#246990] rounded-full"></div>
                              <span className="font-medium">Par semaine</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center">{heuresParSemaine}h</td>
                          <td className="py-3 px-3 text-center">{formatMontant(tauxHoraire)}</td>
                          <td className="py-3 px-3 text-right font-semibold text-blue-700">
                            {formatMontant(gainParSemaine)}
                          </td>
                        </tr>
                        
                        {/* Par mois */}
                        <tr className="hover:bg-gray-50 bg-blue-25">
                          <td className="py-3 px-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-[#246990] rounded-full"></div>
                              <span className="font-medium">Par mois</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center">{(heuresParSemaine * 4).toFixed(1)}h</td>
                          <td className="py-3 px-3 text-center">{formatMontant(tauxHoraire)}</td>
                          <td className="py-3 px-3 text-right font-bold text-[#246990]">
                            {formatMontant(gainParMois)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Informations supplémentaires */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-600">
                      <div className="flex items-center space-x-2">
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        <span>Basé sur 5 jours ouvrés/semaine</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        <span>4 semaines par mois</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        <span>20 jours ouvrés par mois</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Récapitulatif global */}
        <div className="border-t-2 border-gray-200 pt-6">
          <div className="bg-[#246990] text-white rounded-lg p-4 shadow-lg">

            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Calculator className="w-6 h-6" />
                </div>
                <div>
                  <h5 className="text-xl font-bold">Récapitulatif total</h5>
                  <p className="text-blue-100">Toutes classes confondues</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">
                  {formatMontant(
                    role.taux_horaires_predefinis.reduce((total, taux) => 
                      total + ((taux.taux_horaire || 0) * (taux.heures_semaine || 0) * 4), 0
                    )
                  )}
                </div>
                <div className="text-blue-100">par mois</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
               
              <div className="text-center p-3 bg-white/10 rounded-lg">
                <div className="text-2xl font-bold">
                  {formatMontant(
                    role.taux_horaires_predefinis.reduce((total, taux) => 
                      total + ((taux.taux_horaire || 0) * (taux.heures_semaine || 0)), 0
                    )
                  )}
                </div>
                <div className="text-blue-100 text-sm">par semaine</div>
              </div>
              <div className="text-center p-3 bg-white/20 rounded-lg border border-white/30">
                <div className="text-2xl font-bold">
                  {formatMontant(
                    role.taux_horaires_predefinis.reduce((total, taux) => 
                      total + ((taux.taux_horaire || 0) * (taux.heures_semaine || 0) * 4), 0
                    )
                  )}
                </div>
                <div className="text-white text-sm font-medium">par mois</div>
              </div>
            </div>
          </div>
        </div>

        {/* Note explicative */}
        <div className="text-xs text-gray-500 text-center p-3 bg-gray-50 rounded-lg">
          * Calculs basés sur X heures/semaine et 4 semaines par mois.
        </div>
      </div>
    ) : (
      /* Mode mensuel ou pas de classes */
      <div className="text-center py-8">
        <div className="text-gray-400 mb-4">
          <Calculator className="w-12 h-12 mx-auto" />
        </div>
        <h5 className="text-lg font-medium text-gray-600 mb-2">Calculs détaillés indisponibles</h5>
        <p className="text-gray-500">
          {role.mode_remuneration === 'MENSUEL' 
            ? 'Les calculs détaillés sont disponibles uniquement pour la rémunération horaire.'
            : 'Sélectionnez des classes pour voir les calculs détaillés.'
          }
        </p>
      </div>
    )}
  </div>
</div>
            </div>
          )}
        </div>
      ))}
      
      {/* Bouton pour ajouter un rôle supplémentaire */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleAddRole}
          className="px-4 py-2.5 border border-[#246990] text-[#246990] rounded-lg hover:bg-blue-50 flex items-center shadow-sm"
        >
          <PlusCircle className="mr-2" size={18} />
          Ajouter un rôle supplémentaire
        </button>
      </div>

      {/* Message informatif */}
      <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mt-6">
        <div className="p-2 bg-[#246990] rounded-full mr-4">
          <AlertCircle className="text-white" size={20} />
        </div>
        <div className="flex-1">
          <h5 className="font-medium text-[#246990]">Informations importantes</h5>
          <p className="text-sm text-gray-600">Veuillez configurer au moins un rôle avec sa rémunération pour pouvoir passer à l'étape suivante.</p>
        </div>
      </div>
    </div>
  );
};
      
    


  const renderStep3 = () => (
    <div className="space-y-6">
      {renderError()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="p-4 bg-[#eef7fb] border-b border-[#246990]/20 flex items-center gap-3">
            <div className="p-2 bg-[#246990] rounded-full">
              <ImageIcon className="text-white" size={20} />
            </div>
            <div>
              <h3 className="font-medium text-[#246990] text-lg">Photo de l'employé</h3>
              <p className="text-sm text-gray-600">Importez une photo ou prenez-la directement avec la caméra.</p>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {previewImage ? (
              <div className="relative mx-auto w-48 h-48 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shadow-sm">
                <img src={previewImage} alt="Aperçu" className="w-full h-full object-cover" />
                <button type="button" onClick={clearPhoto} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600" title="Retirer la photo">
                  <X size={16} />
                </button>
              </div>
            ) : cameraActive ? (
              <div className="space-y-3">
                <div className="mx-auto w-64 max-w-full aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-900 shadow-sm">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  <button type="button" onClick={handleCapturePhoto} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#246990] text-white hover:bg-[#3084b5]">
                    <Camera size={18} />
                    Capturer
                  </button>
                  <button type="button" onClick={stopCamera} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                    <X size={18} />
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center">
                <Upload className="mx-auto text-gray-400" size={34} />
                <p className="mt-3 text-sm text-gray-600">PNG ou JPG jusqu'à 5MB</p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <label htmlFor="photo" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#246990] text-white hover:bg-[#3084b5] cursor-pointer">
                    <Upload size={18} />
                    Importer une photo
                  </label>
                  <button type="button" onClick={startCamera} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#b8d4e2] bg-[#e7f3f8] text-[#246990] hover:bg-[#d9edf5]">
                    <Camera size={18} />
                    Prendre une photo
                  </button>
                </div>
                <input
                  id="photo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'photo')}
                  className="hidden"
                />
              </div>
            )}

            {cameraError && (
              <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">
                {cameraError}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="p-4 bg-[#eef7fb] border-b border-[#246990]/20 flex items-center gap-3">
            <div className="p-2 bg-[#246990] rounded-full">
              <FileText className="text-white" size={20} />
            </div>
            <div>
              <h3 className="font-medium text-[#246990] text-lg">Documents</h3>
              <p className="text-sm text-gray-600">Ajoutez les pièces justificatives de l'employé.</p>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {employeeDocumentSections.map((section) => {
              const files = getDocumentFiles(section.type);

              return (
                <div key={section.type} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {section.title}
                        {section.required && <span className="text-red-500"> *</span>}
                      </p>
                      <p className="text-xs text-gray-500">{section.hint}</p>
                    </div>
                    <label htmlFor={`employee-doc-${section.type}`} className="px-3 py-2 rounded-md bg-[#e7f3f8] text-[#246990] border border-[#b8d4e2] text-sm font-medium cursor-pointer hover:bg-[#d9edf5] flex-shrink-0">
                      {files.length > 0 && section.multiple ? 'Ajouter' : files.length > 0 ? 'Modifier' : 'Charger'}
                    </label>
                    <input
                      type="file"
                      accept={section.accept}
                      multiple={section.multiple}
                      className="hidden"
                      id={`employee-doc-${section.type}`}
                      onChange={(e) => handleFileChange(e, section.type, { multiple: section.multiple })}
                    />
                  </div>
                  {files.length > 0 && renderDocumentList(files, (index) => removeDocumentFile(section.type, index))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mt-6">
        <div className="p-2 bg-[#246990] rounded-full mr-4">
          <AlertCircle className="text-white" size={20} />
        </div>
        <div className="flex-1">
          <h5 className="font-medium text-[#246990]">Informations importantes</h5>
          <p className="text-sm text-gray-600">La photo d'identité et la pièce d'identité sont des documents obligatoires pour pouvoir passer à l'étape suivante.</p>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      {renderError()}


       {/* Bannière de confirmation */}
       <div className="mt-8 p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center">
          <div className="p-2 bg-[#246990] rounded-full mr-4">
            <Check className="text-white" size={20} />
          </div>
          <div className="flex-1">
            <h5 className="font-medium text-[#246990]">Prêt à finaliser</h5>
            <p className="text-sm text-gray-600">Vérifiez que toutes les informations sont correctes avant de soumettre le formulaire.</p>
          </div>
        </div>

      

      {hasHourlyContractAssignment() && (
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 flex items-start">
          <div className="p-2 bg-orange-100 rounded-full mr-4 mt-0.5">
            <AlertCircle className="text-orange-700" size={20} />
          </div>
          <div className="flex-1">
            <h5 className="font-medium text-orange-800">{"Affectation des mati\u00e8res \u00e0 finaliser"}</h5>
            <p className="text-sm text-orange-700">
              {"Vous avez choisi le type de contrat HORAIRE et renseign\u00e9 les classes enseign\u00e9es. Apr\u00e8s l'enregistrement, allez dans le module Affectation pour associer les mati\u00e8res enseign\u00e9es \u00e0 cet enseignant. Cette \u00e9tape est n\u00e9cessaire pour pouvoir cr\u00e9er les cr\u00e9neaux dans le module Emploi du temps."}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        {/* En-tête avec icône et titre */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#eef7fb] rounded-lg">
              <FileText className="text-[#246990]" size={24} />
            </div>
            <h3 className="text-xl font-medium text-gray-900">Récapitulatif</h3>
          </div>
          <div className="text-sm text-gray-500 bg-[#f8fafc] px-3 py-1 rounded-full">
            Étape finale
          </div>
        </div>
        
        {/* Contenu principal en colonnes avec design amélioré */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Informations personnelles */}
          <div className="rounded-lg bg-[#f8fafc] p-5">
            <div className="flex items-center space-x-2 mb-4">
              <User className="text-[#246990]" size={18} />
              <h4 className="text-base font-medium text-gray-800">Informations personnelles</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Nom</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.nom}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Prénom</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.prenom}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Sexe</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">
                  {formData.sexe === 'M' ? 'Masculin' : formData.sexe === 'F' ? 'Féminin' : formData.sexe || '-'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Email</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.email || '-'}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Téléphone</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{getPhoneSummary()}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-500">Date de naissance</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.date_naissance}</span>
              </div>
            </div>
          </div>
          
          {/* Adresse */}
          <div className="rounded-lg bg-[#f8fafc] p-5">
            <div className="flex items-center space-x-2 mb-4">
              <MapPin className="text-[#246990]" size={18} />
              <h4 className="text-base font-medium text-gray-800">Adresse</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Adresse</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.adresse}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Ville</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.ville}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Code postal</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.code_postal}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Pays</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.pays}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-500">Département</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">
                  {departements.find(d => d.id === formData.departement_id)?.nom || '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Informations professionnelles */}
        <div className="mt-6">
          <div className="rounded-lg bg-[#f8fafc] p-5">
            <div className="flex items-center space-x-2 mb-4">
              <Briefcase className="text-[#246990]" size={18} />
              <h4 className="text-base font-medium text-gray-800">Informations professionnelles</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Date d'embauche</span>
              <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">
                {formData.info_pro?.date_embauche || formData.date_embauche || '-'}
              </span>
            </div>
            
            {/* Type de contrat pour badges/salaires */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Type de contrat</span>
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                (formData.type_contrat === 'HORAIRE' || formData.info_pro?.type_contrat === 'HORAIRE')
                  ? 'bg-orange-100 text-orange-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {(formData.type_contrat || formData.info_pro?.type_contrat) === 'HORAIRE' ? 'HORAIRE' : 'FIXE'}
              </span>
            </div>
            
            {/* Statut légal */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Statut légal</span>
              <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">
                {formData.info_pro?.statut_legal || '-'}
                {formData.info_pro?.statut_legal === 'Autre' && formData.info_pro?.type_contrat_autre ? 
                  ` (${formData.info_pro.type_contrat_autre})` : ''}
              </span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Numéro de badge</span>
              <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.numero_badge || '-'}</span>
            </div>
            
            {/* Horaires standards */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Horaires standards</span>
              <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">
                {formData.horaire_standard_debut || '08:00'} - {formData.horaire_standard_fin || '17:00'}
              </span>
            </div>
            
            {/* Date de fin si CDD */}
            {formData.info_pro?.statut_legal === 'CDD' && formData.info_pro?.date_fin_contrat && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Date de fin (CDD)</span>
                <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">
                  {formData.info_pro.date_fin_contrat}
                </span>
              </div>
            )}
            
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Accès à l'application</span>
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${formData.info_pro?.acces_application ? 
                'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {formData.info_pro?.acces_application ? 'Oui' : 'Non'}
              </span>
            </div>
          </div>
            
            {formData.info_pro?.acces_application && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start space-x-3">
                <div className="p-1 bg-blue-100 rounded-full mt-0.5">
                  <AlertCircle className="text-blue-600" size={16} />
                </div>
                <p className="text-sm text-blue-700">
                  Après la soumission de ce formulaire, la demande d'accès à l'application va être envoyée à la direction pour validation. 
                  Les identifiants de connexion vont déjà être créés.
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Contact d'urgence */}
        {formData.contact_urgence.nom_prenom && (
          <div className="mt-6 rounded-lg bg-[#f8fafc] p-5">
            <div className="flex items-center space-x-2 mb-4">
              <Phone className="text-[#246990]" size={18} />
              <h4 className="text-base font-medium text-gray-800">Contact d'urgence</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.contact_urgence.nom_prenom && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Nom et prénom</span>
                  <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.contact_urgence.nom_prenom}</span>
                </div>
              )}
              {formData.contact_urgence.relation && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Relation</span>
                  <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.contact_urgence.relation}</span>
                </div>
              )}
              {formData.contact_urgence.telephone && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Téléphone</span>
                  <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.contact_urgence.telephone}</span>
                </div>
              )}
              {formData.contact_urgence.email && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Email</span>
                  <span className="text-sm font-medium px-2 py-1 bg-white rounded-md shadow-sm">{formData.contact_urgence.email}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Rôles et rémunération */}
        <div className="mt-6">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="text-[#246990]" size={18} />
            <h4 className="text-base font-medium text-gray-800">Rôles et rémunération</h4>
          </div>
          
          <div className="space-y-4">
            {formData.roles.map((role, index) => (
              <div key={role.id} className="p-5 bg-[#f8fafc] rounded-lg border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                  <div className="flex items-center space-x-2">
                    <div className="w-7 h-7 rounded-full bg-[#246990] text-white flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-800">{index === 0 ? 'Rôle principal' : `Rôle additionnel`}</span>
                  </div>
                  <span className="text-sm bg-white px-3 py-1 rounded-full border border-gray-200 shadow-sm">
                    {groupesSecurite.find(g => g.id === role.groupe_securite_id)?.nom || ''}
                  </span>
                </div>
                
                {/* Afficher les rôles sélectionnés si disponibles */}
                {role.roles_selectionnes && role.roles_selectionnes.length > 0 && (
                  <div className="mb-3">
                    <div className="text-sm text-gray-500 mb-2">Rôles attribués :</div>
                    <div className="flex flex-wrap gap-2">
                      {role.roles_selectionnes.map(selectedRole => (
                        <div key={selectedRole.role_id} className="bg-[#eef7fb] px-2 py-1 rounded-md text-xs text-[#246990] border border-[#246990]/20">
                          {selectedRole.role_nom}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <div className="bg-white px-3 py-1.5 rounded-full border border-gray-200 text-sm shadow-sm flex items-center">
                    {role.mode_remuneration === 'MENSUEL' ? (
                      <>
                        <Calendar className="mr-1.5 text-[#246990]" size={14} />
                        Rémunération mensuelle
                      </>
                    ) : (
                      <>
                        <Clock className="mr-1.5 text-[#246990]" size={14} />
                        Rémunération horaire
                      </>
                    )}
                  </div>
                  
                  {role.mode_remuneration === 'MENSUEL' && (
                    <div className="bg-white px-3 py-1.5 rounded-full border border-gray-200 text-sm shadow-sm flex items-center">
                      <DollarSign className="mr-1.5 text-[#246990]" size={14} />
                      Base: {parseFloat(role.salaire_base) || 0}€
                    </div>
                  )}
                  
                  {role.mode_remuneration === 'HORAIRE' && role.classes_selectionnees.length > 0 && (
                    <div className="bg-white px-3 py-1.5 rounded-full border border-gray-200 text-sm shadow-sm flex items-center">
                      <BookOpen className="mr-1.5 text-[#246990]" size={14} />
                      {role.classes_selectionnees.length} classe(s)
                    </div>
                  )}
                </div>

              </div>
            ))}
          </div>
        </div>
        
        {/* Documents */}
        <div className="mt-6">
          <div className="flex items-center space-x-2 mb-4">
            <File className="text-[#246990]" size={18} />
            <h4 className="text-base font-medium text-gray-800">Documents</h4>
          </div>
          
          <div className="bg-[#f8fafc] rounded-lg p-5 border border-gray-100 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`flex items-center space-x-3 p-3 rounded-lg ${previewImage ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                {previewImage ? (
                  <div className="w-10 h-10 rounded-lg overflow-hidden shadow-sm">
                    <img src={previewImage} alt="Aperçu" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <Upload className="text-red-500" size={20} />
                  </div>
                )}
                <div className="flex-1">
                  <span className="text-sm font-medium">Photo</span>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${previewImage ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className={`text-xs ${previewImage ? 'text-green-600' : 'text-red-600'}`}>
                      {previewImage ? 'Téléchargée' : 'Non téléchargée'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={`flex items-center space-x-3 p-3 rounded-lg ${hasDocument("cv") ? 'bg-green-50 border border-green-100' : 'bg-gray-50 border border-gray-100'}`}>
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <File className={hasDocument("cv") ? 'text-green-500' : 'text-gray-400'} size={20} />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium">CV</span>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${hasDocument("cv") ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className={`text-xs ${hasDocument("cv") ? 'text-green-600' : 'text-gray-500'}`}>
                      {hasDocument("cv") ? 'Téléchargé' : 'Optionnel'}
                    </span>
                  </div>
                </div>
                {hasDocument("cv") && (
                  <span className="text-xs text-gray-500">{getDocumentSummaryLabel("cv")}</span>
                )}
              </div>
              
              <div className={`flex items-center space-x-3 p-3 rounded-lg ${hasDocument("diplome") ? 'bg-green-50 border border-green-100' : 'bg-gray-50 border border-gray-100'}`}>
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <FileText className={hasDocument("diplome") ? 'text-green-500' : 'text-gray-400'} size={20} />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium">Diplôme</span>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${hasDocument("diplome") ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className={`text-xs ${hasDocument("diplome") ? 'text-green-600' : 'text-gray-500'}`}>
                      {hasDocument("diplome") ? 'Téléchargé' : 'Optionnel'}
                    </span>
                  </div>
                </div>
                {hasDocument("diplome") && (
                  <span className="text-xs text-gray-500">{getDocumentSummaryLabel("diplome")}</span>
                )}
              </div>
              
              <div className={`flex items-center space-x-3 p-3 rounded-lg ${hasDocument("piece_identite") ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <File className={hasDocument("piece_identite") ? 'text-green-500' : 'text-red-500'} size={20} />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium">Pièce d'identité</span>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${hasDocument("piece_identite") ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className={`text-xs ${hasDocument("piece_identite") ? 'text-green-600' : 'text-red-600'}`}>
                      {hasDocument("piece_identite") ? 'Téléchargée' : 'Non téléchargée'}
                    </span>
                  </div>
                </div>
                {hasDocument("piece_identite") && (
                  <span className="text-xs text-gray-500">{getDocumentSummaryLabel("piece_identite")}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        

      </div>
    </div>
  );

  // Le serveur exige RESSOURCE_HUMAINES.EMPLOYES.CREATE pour créer une fiche.
  // Sans elle, le formulaire ne serait qu'un piège : quatre étapes de saisie,
  // puis un refus au dernier clic. Placé APRÈS tous les hooks du composant.
  if (!droitsFiche.chargement && !droitsFiche.peutCreer) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div>
          <p className="font-semibold">Création de fiches réservée</p>
          <p className="mt-1 leading-relaxed">
            Créer un employé demande la permission « Créer un employé » des ressources humaines.
            Demandez-la à l'administration si votre fonction l'exige.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6">
          {/* Indicateur d'étapes */}
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step
                      ? 'bg-[#246990] text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step}
                </div>
                {step < 4 && (
                  <div className={`w-16 h-1 ${currentStep > step ? 'bg-[#246990]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}

            {/* Boutons de navigation */}
            <div className="mt-8 flex justify-end gap-4 border-t pt-6">
  {/* Bouton Précédent ou Annuler */}
  {currentStep > 1 ? (
    <button
      type="button"
      onClick={() => setCurrentStep(prev => prev - 1)}
      className="px-6 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
    >
      Précédent
    </button>
  ) : (
    <button
      type="button"
      onClick={resetForm}
      className="px-6 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
    >
      <RefreshCw size={18} />
      Annuler
    </button>
  )}

  {/* Bouton Suivant uniquement pour les étapes 1, 2 et 3 */}
  {currentStep < 4 && (
    <button
      type="button"
      onClick={goToNextStep}
      className="px-6 py-2.5 text-white bg-[#246990] rounded-lg hover:bg-[#3084b5] flex items-center gap-2"
    >
      Suivant
    </button>
  )}
  
  {/* Bouton Enregistrer uniquement pour l'étape 4 */}
  {currentStep === 4 && (
    <button
      type="submit"
      disabled={loading}
      aria-busy={loading}
      className={`px-6 py-2.5 text-white rounded-lg flex items-center gap-2 transition-colors ${
        loading
          ? 'bg-[#246990]/70 cursor-not-allowed'
          : 'bg-[#246990] hover:bg-[#3084b5]'
      }`}
    >
      {loading ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
      {loading ? 'Enregistrement...' : 'Enregistrer'}
    </button>
  )}
</div>
          </form>
        </div>
      </div>

      {showCredentials && (
  <CredentialsModal
    credentials={credentials}
    setToastInfo={setToastInfo}
    onClose={(shouldReset = false) => {
      setShowCredentials(false);
      
      // Si shouldReset est true, on réinitialise le formulaire
      if (shouldReset) {
        resetForm(); // Appelle la méthode resetForm du composant
      }
      
      if (typeof onClose === 'function') {
        onClose(true);
      }
    }}
  />
)}

      {toastInfo && (
        shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo) ? (
          <LongToast
            show
            title={toastInfo.title}
            message={toastInfo.message}
            type={toastInfo.type}
            onClose={() => setToastInfo(null)}
          />
        ) : (
          <Toast
            message={toastInfo.message}
            type={toastInfo.type}
            onClose={() => setToastInfo(null)}
          />
        )
      )}
    </div>
  );
};

export default NewEmployeeForm